// ============================================================================
// Componente: RTB12SimplesNacional.js
// Objetivo: Interface para controle do limite do Simples Nacional, Rateio Orçado, Rolling Forecast e Rateio Notas.
// ============================================================================

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import {
    DollarSign, Save, AlertCircle, CheckCircle2,
    ArrowLeft, Loader2, HelpCircle, Calendar,
    PieChart, LayoutDashboard, Settings, Info,
    UploadCloud, Trash2, TrendingUp, Download, Receipt,
    SlidersHorizontal, History,
    ArrowRightLeft, X, FileSpreadsheet
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import HelpCenter from '../../HelpCenter';
import * as XLSX from 'xlsx';
import { obterReceitaBruta, acumuladoAnoRealizado, acumuladoAnoAteVigente, enquadramentoRBA, ultimoMesComReceita, projecaoFimAnoRitmo, ritmoEAjuste, periodoMesAnteriorAoCorrente } from './rtb12-utils';

const SUB_LIMITE = 3600000;
const LIMITE = 4800000;
// Sobra-alvo por empresa no fim do ano (folga p/ dezembro/13º). O ritmo linear de
// projeção mira um teto efetivo = LIMITE − SOBRA_ALVO, distribuído em 12 meses.
const SOBRA_ALVO = 700000;
const TETO_EFETIVO = LIMITE - SOBRA_ALVO; // 4.100.000
const EMPRESAS = ['PARTNER', 'PRT', 'PTN', 'PSM', 'PGO'];
// Prestadores (grupos de serviço) que recebem o rateio das notas — antes eram o filtro
// da aba Rateio Notas; hoje viram as LINHAS da tabela (o filtro passou a ser o emitente).
const PRESTADORES = ['HUNGER', 'P&G', 'GC FOOD', 'P&M'];
const corModulo = '#16a34a';
const corRolling = '#0ea5e9';
const corSap = '#0d9488'; // teal — identidade visual da aba Template SAP

// ============================================================================
// MAPA_SAP — esqueleto do lançamento SAP por prestador (transcrito do RTB12_template.xlsx)
// ----------------------------------------------------------------------------
// A "base" de cada bloco (emitente) vem da tela Rateio Notas (construirNotasEmpresa),
// então é dinâmica por período. Aqui ficam só os METADADOS FIXOS (item, descrição,
// conta, código de imposto, filial) e a REGRA de valor de cada conta:
//   • fonte.col  → soma de colunas da tela (a conta do SAP existe na Rateio Notas):
//                  usa o valor calculado pela tela (ex.: PRT nota 1, medicina, PSM).
//   • fonte.frac → fração fixa do total do emitente (a conta só existe no layout SAP):
//                  PTN, PGO e o PARTNER 2 de P&G/GC (sem conta de TI). As frações de
//                  um bloco somam 100% → não perde nem cria dinheiro.
// Decisão 2026-07-06 (Julia): "a tela Rateio Notas manda" — por isso os splits `col`
// podem divergir do xlsx original (que é um snapshot antigo). Ver card no PLANO.md.
// ============================================================================
const SAP_DESC = {
    S00036: 'ADMINISTRACAO EM GERAL E APOIO AO NEGOCIO',
    S00037: 'PLANEJAMENTO E COORDENACAO DA ROTINA FINANCEIRA E ADM',
    S00038: 'MANUTENCAO PREVENTIVA, CORRETIVA DE MAQUINAS E EQUIP.',
    S00039: 'HIGIENIZAÇÃO DO RESTAURANTE E LIMPEZA DE MAQUINAS E EQUIPAMENTOS',
    S00064: 'SERVICO PROMOCAO DE VENDAS',
    S00070: 'MEDICINA E SAUDE OCUPACIONAL'
};

// Blocos de P&G e GC Food são estruturalmente iguais (mudam só filial/valores e,
// no P&G, o código de imposto — ambos 1933-005 aqui). Fábrica p/ não repetir.
const blocosPGeGC = (imp = '1933-005') => ([
    { emitente: 'PRT', rows: [
        { item: 'S00038', conta: '41160100000003', imp, fonte: { col: [['PRT - Nota 1', '41160100000003']] } },
        { item: 'S00038', conta: '41140400000013', imp, fonte: { col: [['PRT - Nota 1', '41140400000013']] } }
    ] },
    { emitente: 'PRT', rows: [
        { item: 'S00070', conta: '41110600000008', imp, fonte: { col: [['PRT - Nota 2', '41110600000008']] } }
    ] },
    { emitente: 'PTN', rows: [
        { item: 'S00039', conta: '41140400000013', imp, fonte: { frac: 0.13, base: 'PTN' } },
        { item: 'S00039', conta: '41140400000015', imp, fonte: { frac: 0.87, base: 'PTN' } }
    ] },
    { emitente: 'PARTNER', rows: [
        { item: 'S00037', conta: '41410100000018', imp, fonte: { col: [['PARTNER 1', '41410100000018']] } },
        { item: 'S00037', conta: '41140400000013', imp, fonte: { col: [['PARTNER 1', '41140400000013']] } }
    ] },
    { emitente: 'PARTNER', rows: [
        { item: 'S00036', conta: '41140400000002', imp, fonte: { frac: 0.86, base: 'PARTNER 2' } },
        { item: 'S00036', conta: '41140400000013', imp, fonte: { frac: 0.14, base: 'PARTNER 2' } }
    ] },
    { emitente: 'PSM', rows: [
        { item: 'S00064', conta: '41250100000001', imp, fonte: { col: [['PSM - MKT', '41250100000001']] } },
        { item: 'S00036', conta: '41140400000015', imp, fonte: { col: [['PSM - MKT', '41140400000015']] } }
    ] },
    { emitente: 'PGO', rows: [
        { item: 'S00036', conta: '41140400000002', imp, fonte: { frac: 0.86, base: 'PGO - PARTNER' } },
        { item: 'S00036', conta: '41140400000015', imp, fonte: { frac: 0.14, base: 'PGO - PARTNER' } }
    ] }
]);

const MAPA_SAP = {
    'P&G': {
        filial: 'VSH-P&G Comércio de Alimentos Ltda',
        blocos: blocosPGeGC('1933-005')
    },
    'GC FOOD': {
        filial: 'MTG- GC FOOD Comércio de Alimentos Ltda',
        blocos: blocosPGeGC('1933-005')
    },
    'HUNGER': {
        filial: 'ESCRITORIO-Hunger Comércio de Alimentos Ltda',
        blocos: [
            { emitente: 'PRT', rows: [
                { item: 'S00038', conta: '41160100000003', imp: '1933-004', fonte: { col: [['PRT - Nota 1', '41160100000003']] } },
                { item: 'S00038', conta: '41140400000013', imp: '1933-004', fonte: { col: [['PRT - Nota 1', '41140400000013']] } }
            ] },
            { emitente: 'PRT', rows: [
                { item: 'S00070', conta: '41110600000008', imp: '1933-005', fonte: { col: [['PRT - Nota 2', '41110600000008']] } }
            ] },
            { emitente: 'PTN', rows: [
                { item: 'S00039', conta: '41140400000007', imp: '1933-005', fonte: { frac: 0.76, base: 'PTN' } },
                { item: 'S00039', conta: '41140200000001', imp: '1933-005', fonte: { frac: 0.10, base: 'PTN' } },
                { item: 'S00039', conta: '41140400000013', imp: '1933-005', fonte: { frac: 0.14, base: 'PTN' } }
            ] },
            // PARTNER 1 e PARTNER 2 em blocos SEPARADOS (2026-07-14, pedido da Contabilidade) —
            // antes vinham fundidos num bloco só (herança do template antigo do HUNGER), o que
            // obrigava a somar a conta 013 das duas notas numa linha. Agora cada nota é um bloco,
            // igual aos demais prestadores: o valor total não muda, só a organização das linhas.
            { emitente: 'PARTNER', rows: [
                { item: 'S00037', conta: '41410100000018', imp: '1933-004', fonte: { col: [['PARTNER 1', '41410100000018']] } },
                { item: 'S00037', conta: '41140400000013', imp: '1933-004', fonte: { col: [['PARTNER 1', '41140400000013']] } }
            ] },
            { emitente: 'PARTNER', rows: [
                { item: 'S00036', conta: '41140400000002', imp: '1933-004', fonte: { col: [['PARTNER 2', '41140400000002']] } },
                { item: 'S00036', conta: '41140400000013', imp: '1933-004', fonte: { col: [['PARTNER 2', '41140400000013']] } },
                { item: 'S00036', conta: '41180400000003', imp: '1933-004', fonte: { col: [['PARTNER 2', '41180400000003']] } }
            ] },
            { emitente: 'PSM', rows: [
                { item: 'S00064', conta: '41250100000001', imp: '1933-005', fonte: { col: [['PSM - MKT', '41250100000001']] } },
                { item: 'S00036', conta: '41140400000015', imp: '1933-005', fonte: { col: [['PSM - MKT', '41140400000015']] } }
            ] },
            { emitente: 'PGO', rows: [
                { item: 'S00036', conta: '41140400000002', imp: '1933-004', fonte: { frac: 0.70, base: 'PGO - PARTNER' } },
                { item: 'S00036', conta: '41140400000015', imp: '1933-004', fonte: { frac: 0.30, base: 'PGO - PARTNER' } }
            ] }
        ]
    },
    'P&M': {
        filial: 'MTP- P&M Correia Comércio de Alimentos Ltda',
        blocos: [
            { emitente: 'PRT', rows: [
                { item: 'S00038', conta: '41160100000003', imp: '1933-004', fonte: { col: [['PRT - Nota 1', '41160100000003']] } },
                { item: 'S00038', conta: '41140400000013', imp: '1933-004', fonte: { col: [['PRT - Nota 1', '41140400000013']] } }
            ] },
            { emitente: 'PRT', rows: [
                { item: 'S00070', conta: '41110600000008', imp: '1933-005', fonte: { col: [['PRT - Nota 2', '41110600000008']] } }
            ] },
            { emitente: 'PTN', rows: [
                { item: 'S00039', conta: '41140400000007', imp: '1933-005', fonte: { frac: 0.76, base: 'PTN' } },
                { item: 'S00039', conta: '41140200000001', imp: '1933-005', fonte: { frac: 0.10, base: 'PTN' } },
                { item: 'S00039', conta: '41140400000013', imp: '1933-005', fonte: { frac: 0.14, base: 'PTN' } }
            ] },
            // No P&M o PARTNER 2 tem a conta de TI (41180400000003) → estrutura bate
            // com a tela, então tudo `col` (sem fração fixa).
            { emitente: 'PARTNER', rows: [
                { item: 'S00037', conta: '41410100000018', imp: '1933-004', fonte: { col: [['PARTNER 1', '41410100000018']] } },
                { item: 'S00037', conta: '41140400000013', imp: '1933-004', fonte: { col: [['PARTNER 1', '41140400000013']] } }
            ] },
            { emitente: 'PARTNER', rows: [
                { item: 'S00036', conta: '41140400000002', imp: '1933-004', fonte: { col: [['PARTNER 2', '41140400000002']] } },
                { item: 'S00036', conta: '41140400000013', imp: '1933-004', fonte: { col: [['PARTNER 2', '41140400000013']] } },
                { item: 'S00036', conta: '41180400000003', imp: '1933-004', fonte: { col: [['PARTNER 2', '41180400000003']] } }
            ] },
            { emitente: 'PSM', rows: [
                { item: 'S00064', conta: '41250100000001', imp: '1933-005', fonte: { col: [['PSM - MKT', '41250100000001']] } },
                { item: 'S00036', conta: '41140400000015', imp: '1933-005', fonte: { col: [['PSM - MKT', '41140400000015']] } }
            ] },
            { emitente: 'PGO', rows: [
                { item: 'S00036', conta: '41140400000002', imp: '1933-004', fonte: { frac: 0.70, base: 'PGO - PARTNER' } },
                { item: 'S00036', conta: '41140400000015', imp: '1933-004', fonte: { frac: 0.30, base: 'PGO - PARTNER' } }
            ] }
        ]
    }
};

// Ordem das abas no Excel/UI — igual ao RTB12_template.xlsx.
const ORDEM_PRESTADORES_SAP = ['P&G', 'HUNGER', 'GC FOOD', 'P&M'];

// Colunas do lançamento SAP — ordem do arquivo "ORDEM TEMPLATE JULY", trazendo os
// dados só até "Regra de distribuição" (as colunas seguintes do SAP não são usadas).
//
// ⚠️ Regra de preenchimento (2026-07-14, Contabilidade): só CINCO colunas trazem dado —
// Nº do item · Preço unitário · Utilização · Conta do Razão · Regra de distribuição.
// TODAS as demais saem EM BRANCO no arquivo (o SAP as calcula/preenche no import).
// Em especial: o valor rateado agora vai no **Preço unitário** (antes ia em Total (MC),
// que hoje sai vazio), e Descrição/Código de imposto/CFOP deixaram de ser exportados —
// SAP_DESC e o campo `imp` do MAPA_SAP seguem no código só como referência do template.
const COLUNAS_SAP = [
    'Nº do item', 'Descrição do item', 'Preço unitário', '% do desconto',
    'Utilização', 'Sujeito a IRF', 'Código de imposto', 'CFOP', 'CST para ICMS',
    'Quantidade', 'Unidade de medida de estoque', 'Total (MC)', 'Qtd.confirmada em Dep.',
    'Conta do Razão', 'Valor do imposto (MC)', 'Regra de distribuição'
];

// Valor fixo da coluna "Utilização" em todo lançamento (o grupo só toma serviço).
const SAP_UTILIZACAO = 'Servico Tomado';

// Função de segurança para parsear números do BD
const parseNum = (val) => {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
        const clean = val.replace(',', '.');
        const parsed = Number(clean);
        return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
};

// Valores padrão usados como fallback enquanto o BD não é carregado
const lojasPorEmpresaDefault = {
    'HUNGER': [
        { loja: 'FER', cc: '112', rateio: 0.1790 },
        { loja: 'VEL', cc: '116', rateio: 0.1683 },
        { loja: 'RDP', cc: '113', rateio: 0.0992 },
        { loja: 'SED', cc: '114', rateio: 0.2180 },
        { loja: 'ANV', cc: '115', rateio: 0.1894 },
        { loja: 'GTV', cc: '142', rateio: 0.1461 }
    ],
    'P&G': [
        { loja: 'VSH', cc: '120', rateio: 0.2355 },
        { loja: 'BVV', cc: '117', rateio: 0.1523 },
        { loja: 'SVV', cc: '118', rateio: 0.2112 },
        { loja: 'MOX', cc: '143', rateio: 0.1986 },
        { loja: 'MAL', cc: '146', rateio: 0.2024 }
    ],
    'GC FOOD': [
        { loja: 'GC', cc: '119', rateio: 1.0000 }
    ],
    'P&M': [
        { loja: 'P&M', cc: '114', rateio: 1.0000 }
    ]
};

// `embedded`: quando true, a tela é renderizada DENTRO do módulo Contabilidade
// (que já fornece Sidebar + cabeçalho). Aí escondemos a Sidebar/cabeçalho/offset
// próprios para não duplicar o casco. Default false mantém o modo página inteira.
const RTB12SimplesNacional = ({ embedded = false }) => {
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('resumo');

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const nomeUsuario = user?.nome ? user.nome.split(' ')[0] : 'Colaborador';
    const podeEditarRateio = ['admin', 'contabil', 'gerente adm'].includes((user?.perfil || '').toLowerCase());

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getMarginLeft = () => isMobile ? '0px' : (isCollapsed ? '80px' : '260px');

    // ⚠️ Isto era `useState('2026-04')` — uma data FIXA no código. A tela abria
    //    em abril de 2026 para sempre, enquanto o banco já tinha agosto: quem
    //    entrava via o Controle do Limite parado cinco meses atrás e precisava
    //    trocar o mês na mão toda vez. Agora nasce no mês ANTERIOR ao corrente,
    //    a mesma definição que a Home usa para cobrar a importação (ver
    //    `periodoMesAnteriorAoCorrente`, testada em rtb12-utils.test.js).
    const [periodoAtual, setPeriodoAtual] = useState(periodoMesAnteriorAoCorrente);
    // Inicia em PARTNER (não "Todas"): o limite é por CNPJ, e o consolidado esconde estouro individual.
    const [empresaRolling, setEmpresaRolling] = useState('PARTNER');
    // Ano selecionado na aba Controle do Limite (o limite do Simples é anual).
    const [anoRolling, setAnoRolling] = useState(() => Number(periodoAtual.split('-')[0]));
    // Emitente selecionado na aba Rateio Notas (antes era o prestador; agora é o emitente/nota).
    const [empresaRateioNotas, setEmpresaRateioNotas] = useState('PRT - Nota 1');
    // Prestador selecionado na aba Template SAP (uma "aba SAP" por prestador).
    const [prestadorSap, setPrestadorSap] = useState('P&G');

    // Rateio dinâmico carregado do banco de dados
    const [lojasPorEmpresa, setLojasPorEmpresa] = useState(lojasPorEmpresaDefault);
    const [rateioRawDB, setRateioRawDB] = useState([]); // linhas brutas com id para atualização
    const [rateioEditando, setRateioEditando] = useState({}); // { [id]: string } edições pendentes
    const [logRateio, setLogRateio] = useState([]);
    const [carregandoRateio, setCarregandoRateio] = useState(false);
    const [carregandoLog, setCarregandoLog] = useState(false);
    const [salvandoRateio, setSalvandoRateio] = useState(false);

    // Modal de transferência de loja
    // null = fechado | { id, loja, cc, empresaOrigem, percentualAtual, empresaDestino, percentualNovo, confirmando }
    const [modalTransf, setModalTransf] = useState(null);

    const [dadosHistoricos, setDadosHistoricos] = useState([]);
    const [projecoes, setProjecoes] = useState({});
    const [ajustesRateio, setAjustesRateio] = useState({});
    const [ajustesRaw, setAjustesRaw] = useState({});

    const [temDadosReais, setTemDadosReais] = useState(true);
    const [temProjecao, setTemProjecao] = useState(false);

    const [aliquotas, setAliquotas] = useState({
        hunger: 48.65,
        pg: 40.36,
        gc: 6.24,
        pm: 4.75
    });

    const [carregando, setCarregando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [mensagem, setMensagem] = useState(null);

    const subtrairMeses = (periodo, meses) => {
        const [ano, mes] = periodo.split('-').map(Number);
        const data = new Date(ano, mes - 1 - meses, 1);
        return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
    };

    const formatarDataBr = (periodo) => {
        if (!periodo) return '';
        const [ano, mes] = periodo.split('-');
        const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
        return `${meses[Number(mes) - 1]}/${ano.substring(2)}`;
    };

    const formatarMesAnoCurto = (periodo) => {
        if (!periodo) return '';
        const [ano, mes] = periodo.split('-');
        const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        return `${meses[Number(mes) - 1]}${ano.substring(2)}`;
    };

    const carregarDados = async () => {
        setCarregando(true);
        try {
            const response = await fetch('/api/contabilidade/rtb12-dados', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            const data = await response.json();
            if (Array.isArray(data)) {
                setDadosHistoricos(data);
                sincronizarProjecoes(data, periodoAtual);
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro ao carregar os dados financeiros.' });
        } finally {
            setCarregando(false);
        }
    };

    const carregarRateio = async () => {
        setCarregandoRateio(true);
        try {
            const response = await fetch('/api/contabilidade/rtb12-rateio', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            if (!response.ok) return;
            const rows = await response.json();
            if (!Array.isArray(rows) || rows.length === 0) return;

            setRateioRawDB(rows);

            // Reconstrói o mapa lojasPorEmpresa a partir das linhas do BD
            const mapa = {};
            rows.forEach(row => {
                if (!mapa[row.empresa]) mapa[row.empresa] = [];
                mapa[row.empresa].push({
                    id: row.id,
                    loja: row.loja,
                    cc: row.cc,
                    rateio: parseFloat(row.percentual)
                });
            });
            setLojasPorEmpresa(mapa);
            setRateioEditando({});
        } catch (error) {
            console.error('Erro ao carregar rateio:', error);
        } finally {
            setCarregandoRateio(false);
        }
    };

    const carregarLogRateio = async () => {
        setCarregandoLog(true);
        try {
            const response = await fetch('/api/contabilidade/rtb12-rateio-log', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            if (!response.ok) return;
            const rows = await response.json();
            setLogRateio(Array.isArray(rows) ? rows : []);
        } catch (error) {
            console.error('Erro ao carregar log de rateio:', error);
        } finally {
            setCarregandoLog(false);
        }
    };

    useEffect(() => {
        carregarDados();
        carregarRateio();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeTab === 'controle-rateio' && podeEditarRateio) {
            carregarLogRateio();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const sincronizarProjecoes = (historico, periodoFiltro) => {
        const projecoesCarregadas = {};
        const periodoMesAnterior = subtrairMeses(periodoFiltro, 1);

        const temDadosReaisPeriodo = historico.some(d => d.Periodo === periodoFiltro && (d.CC === 'RECEITA' || d.CC === 'DESPESA'));
        const temProjecaoPeriodo = historico.some(d => d.Periodo === periodoFiltro && d.CC === 'PROJECAO');

        setTemDadosReais(temDadosReaisPeriodo);
        setTemProjecao(temProjecaoPeriodo);

        EMPRESAS.forEach(emp => {
            const pSalva = historico.find(d => d.Empresa === emp && d.Periodo === periodoFiltro && d.CC === 'PROJECAO');
            if (pSalva) {
                projecoesCarregadas[emp] = parseNum(pSalva.Credito);
            } else {
                // Default = mês anterior + 5%. A projeção do LIMITE (ritmo linear) NÃO entra
                // aqui de propósito: ela zerava empresas acima do ritmo e derrubava a REC
                // ESTIMADA do Rateio Orçado. O controle de limite vive só na aba Controle do
                // Limite e no alerta "Situação (Simples)".
                const recData = historico.find(d => d.Empresa === emp && d.Periodo === periodoMesAnterior && d.CC === 'RECEITA') || {};
                const receitaBruta = parseNum(recData.Credito);
                projecoesCarregadas[emp] = receitaBruta * 1.05;
            }
        });

        setProjecoes(projecoesCarregadas);
        setAjustesRateio({});
        setAjustesRaw({});
    };

    const handlePeriodoChange = (e) => {
        const novoPeriodo = e.target.value;
        setPeriodoAtual(novoPeriodo);
        sincronizarProjecoes(dadosHistoricos, novoPeriodo);
        setMensagem(null);
    };

    const handleAjusteChange = (empresa, valor) => {
        const hasMinus = valor.includes('-');
        const digits = valor.replace(/\D/g, '');
        let num = Number(digits) / 100;
        if (hasMinus) num = -num;

        setAjustesRateio(prev => ({ ...prev, [empresa]: num }));
        setAjustesRaw(prev => ({ ...prev, [empresa]: hasMinus && num === 0 ? '-' : null }));

        const realizado = getRateioRealizado(dadosHistoricos, empresa, subtrairMeses(periodoAtual, 1));
        const valorBase = realizado.receitaBruta * 1.05;
        setProjecoes(prev => ({ ...prev, [empresa]: valorBase + num }));
    };

    const handleRateioChange = (id, valor) => {
        const limpo = valor.replace(/[^0-9,]/g, '');
        setRateioEditando(prev => ({ ...prev, [id]: limpo }));
    };

    const salvarRateio = async () => {
        const alteracoes = [];

        rateioRawDB.forEach(row => {
            const valorEditado = rateioEditando[row.id];
            if (valorEditado === undefined || valorEditado === '') return;
            const novoPercentual = parseFloat(valorEditado.replace(',', '.')) / 100;
            const anteriorPercentual = parseFloat(row.percentual);
            if (Math.abs(novoPercentual - anteriorPercentual) < 0.000001) return;
            alteracoes.push({
                id: row.id,
                empresa: row.empresa,
                loja: row.loja,
                percentual_anterior: anteriorPercentual,
                percentual_novo: novoPercentual
            });
        });

        if (alteracoes.length === 0) {
            setMensagem({ type: 'error', text: 'Nenhuma alteração detectada para salvar.' });
            setTimeout(() => setMensagem(null), 4000);
            return;
        }

        setSalvandoRateio(true);
        try {
            const response = await fetch('/api/contabilidade/rtb12-rateio', {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ alteracoes })
            });
            if (response.ok) {
                setMensagem({ type: 'success', text: 'Percentuais de rateio atualizados com sucesso!' });
                await carregarRateio();
                await carregarLogRateio();
            } else {
                const err = await response.json();
                setMensagem({ type: 'error', text: err.error || 'Erro ao salvar o rateio.' });
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro de conexão com o servidor.' });
        } finally {
            setSalvandoRateio(false);
            setTimeout(() => setMensagem(null), 5000);
        }
    };

    const confirmarTransferencia = async () => {
        if (!modalTransf || !modalTransf.empresaDestino || !modalTransf.percentualNovo) return;
        const percentualNovo = parseFloat(modalTransf.percentualNovo.replace(',', '.')) / 100;
        if (isNaN(percentualNovo) || percentualNovo <= 0) {
            setMensagem({ type: 'error', text: 'Informe um percentual válido para o grupo destino.' });
            setTimeout(() => setMensagem(null), 4000);
            return;
        }
        setModalTransf(prev => ({ ...prev, confirmando: true }));
        try {
            const response = await fetch('/api/contabilidade/rtb12-rateio-transferir', {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: modalTransf.id,
                    empresa_destino: modalTransf.empresaDestino,
                    percentual_novo: percentualNovo
                })
            });
            const data = await response.json();
            if (response.ok) {
                setMensagem({ type: 'success', text: data.message });
                setModalTransf(null);
                await carregarRateio();
                await carregarLogRateio();
            } else {
                setMensagem({ type: 'error', text: data.error || 'Erro ao transferir a loja.' });
                setModalTransf(prev => ({ ...prev, confirmando: false }));
            }
        } catch {
            setMensagem({ type: 'error', text: 'Erro de conexão com o servidor.' });
            setModalTransf(prev => ({ ...prev, confirmando: false }));
        }
        setTimeout(() => setMensagem(null), 5000);
    };

    const handleAliquotaChange = (campo, valor) => {
        const val = valor.replace(/[^0-9,]/g, '').replace(',', '.');
        setAliquotas(prev => ({ ...prev, [campo]: val }));
    };

    const salvarProjecoes = async () => {
        setSalvando(true);
        setMensagem(null);

        try {
            const periodoMesAnterior = subtrairMeses(periodoAtual, 1);
            const payload = EMPRESAS.map(emp => {
                const realizado = getRateioRealizado(dadosHistoricos, emp, periodoMesAnterior);
                const recEstimada = projecoes[emp] || 0;
                const dasEstimado = recEstimada * (realizado.aliquotaEfetiva / 100);

                return {
                    empresa: emp,
                    credito: Number(recEstimada.toFixed(2)),
                    debito: Number(dasEstimado.toFixed(2))
                };
            });

            const response = await fetch('/api/contabilidade/rtb12-inserir', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ periodo: periodoAtual, dados: payload })
            });

            if (response.ok) {
                setMensagem({ type: 'success', text: 'Projeções salvas com sucesso!' });
                await carregarDados();
            } else {
                const err = await response.json();
                setMensagem({ type: 'error', text: err.error || 'Erro ao salvar os dados.' });
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro de conexão com o servidor.' });
        } finally {
            setSalvando(false);
            setTimeout(() => setMensagem(null), 5000);
        }
    };

    const removerProjecoes = async () => {
        if (!window.confirm(`Tem certeza que deseja remover os ajustes de projeção para o período ${formatarDataBr(periodoAtual)}?`)) return;
        setCarregando(true);
        try {
            const response = await fetch(`/api/contabilidade/rtb12-projecao/${periodoAtual}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            if (response.ok) {
                setMensagem({ type: 'success', text: 'Projeções removidas com sucesso!' });
                await carregarDados();
            } else {
                setMensagem({ type: 'error', text: 'Erro ao remover projeções.' });
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro de conexão.' });
        } finally {
            setCarregando(false);
        }
    };

    const baixarPlanilhaBase = () => {
        // ⚠️ O período do exemplo acompanha o mês de trabalho, não uma data fixa:
        //    a planilha-base é preenchida por cima pela contadora, e um exemplo
        //    datado de meses atrás é copiado junto — vira linha importada no
        //    período errado, que o banco aceita sem reclamar.
        const periodoExemplo = periodoAtual;
        const dadosExemplo = [
            { Periodo: periodoExemplo, Empresa: 'PARTNER', CC: 'RECEITA', Debito: 0, Credito: 150000.50, Saldo_Final: -150000.50 },
            { Periodo: periodoExemplo, Empresa: 'PARTNER', CC: 'DESPESA', Debito: 50000.00, Credito: 0, Saldo_Final: 50000.00 }
        ];

        const worksheet = XLSX.utils.json_to_sheet(dadosExemplo);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Base_Importacao");
        XLSX.writeFile(workbook, "Planilha_Base_RTB12.xlsx");
    };

    // Lê a aba montando os objetos com o cabeçalho CRU (o VALOR da célula), e
    // não o texto formatado.
    //
    // ⚠️ PEGADINHA QUE JÁ CUSTOU DADO ERRADO (2026-08-21): o SheetJS batiza as
    // colunas pelo texto EXIBIDO. Se a linha do cabeçalho estiver com o formato
    // numérico "Contábil", a seção de texto desse formato (`_-@_-`) acolchoa o
    // nome com um espaço de cada lado — e `Debito` chega como `" Debito "`. O
    // import então não achava a coluna, gravava R$ 0,00 e dizia "sucesso".
    // `header: 1` + `raw: true` devolve as linhas como array de valores crus,
    // então o nome da coluna sai limpo, independente da formatação da célula.
    const lerAbaComCabecalhoCru = (worksheet) => {
        const matriz = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true, defval: null });
        if (matriz.length < 2) return [];
        const cabecalhos = (matriz[0] || []).map((c) => (c === null || c === undefined ? '' : String(c).trim()));
        return matriz.slice(1).map((linha) => {
            const obj = {};
            cabecalhos.forEach((nome, i) => {
                if (nome === '') return;
                // `null` (e não `undefined`) de propósito: é isso que permite ao
                // backend separar "a coluna não existe na planilha" (recusa) de
                // "a célula está em branco" (zero legítimo).
                obj[nome] = linha[i] === undefined ? null : linha[i];
            });
            return obj;
        });
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Identifica se é um CSV pela extensão
        const isCSV = file.name.toLowerCase().endsWith('.csv');
        const reader = new FileReader();

        reader.onload = async (event) => {
            try {
                let jsonDados = [];

                if (isCSV) {
                    const csvText = event.target.result;
                    // Separa as linhas (compatível com quebras do Windows \r\n e Unix \n)
                    const linhas = csvText.split(/\r?\n/).filter(linha => linha.trim() !== '');

                    if (linhas.length > 1) {
                        // Identifica dinamicamente o delimitador (padrão Brasil ';' vs Padrão Internacional ',')
                        const delimitador = linhas[0].includes(';') ? ';' : ',';

                        // Captura e limpa os cabeçalhos removendo aspas duplas, se houver
                        const cabecalhos = linhas[0].split(delimitador).map(c => c.trim().replace(/^"|"$/g, ''));

                        for (let i = 1; i < linhas.length; i++) {
                            const valores = linhas[i].split(delimitador).map(v => v.trim().replace(/^"|"$/g, ''));
                            let obj = {};
                            cabecalhos.forEach((cabecalho, index) => {
                                obj[cabecalho] = valores[index] !== undefined ? valores[index] : null;
                            });
                            jsonDados.push(obj);
                        }
                    }
                } else {
                    // Leitura normal via SheetJS para arquivos nativos do Excel (.xlsx / .xls)
                    const dataRaw = new Uint8Array(event.target.result);
                    const workbook = XLSX.read(dataRaw, { type: 'array' });
                    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                    jsonDados = lerAbaComCabecalhoCru(worksheet);
                }

                console.log("[DEBUG FRONTEND] Dados extraídos:", jsonDados);

                if (!jsonDados || jsonDados.length === 0) {
                    setMensagem({ type: 'error', text: 'O arquivo foi lido, mas a aba está vazia ou a formatação está incorreta.' });
                    return;
                }

                setCarregando(true);

                const response = await fetch('/api/contabilidade/rtb12-importar-excel', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ dados: jsonDados })
                });

                if (response.ok) {
                    const result = await response.json();
                    // Mostra de volta o TOTAL que entrou, não só "sucesso".
                    // É a conferência mais barata contra o arquivo ter sido lido
                    // errado — foi a falta disso que deixou "importado com
                    // sucesso" esconder R$ 1,7 milhão virando zero em 2026-08-21.
                    const r = result.resumo;
                    const texto = r
                        ? `${r.linhas} linha(s) importada(s) — ${r.periodos.join(', ')} · `
                          + `${r.empresas.length} empresa(s) · Receita: R$ ${formatMoney(r.totalReceita)} · `
                          + `DAS: R$ ${formatMoney(r.totalDas)} · Despesa: R$ ${formatMoney(r.totalDespesa)}. `
                          + 'Confira os totais contra a planilha.'
                        : (result.message || 'Dados importados com sucesso!');
                    setMensagem({ type: 'success', text: texto });
                    await carregarDados();
                } else {
                    const err = await response.json();
                    setMensagem({ type: 'error', text: err.error || 'Erro ao importar arquivo.' });
                }
            } catch (error) {
                console.error("Erro no frontend:", error);
                setMensagem({ type: 'error', text: 'Erro na conversão do arquivo.' });
            } finally {
                setCarregando(false);
            }
        };

        // Direciona o método correto de leitura
        if (isCSV) {
            // Ler como texto garante que a gente consiga fazer o split manual do CSV
            reader.readAsText(file);
        } else {
            reader.readAsArrayBuffer(file);
        }

        e.target.value = null;
    };


    // --- CÁLCULOS MATEMÁTICOS BASE ---

    const formatMoney = (valor) => {
        const num = parseNum(valor);
        const formatado = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(num));
        return num < 0 ? `(${formatado})` : formatado;
    };

    const obterReceitaBrutaReal = (empresa, periodo) => obterReceitaBruta(dadosHistoricos, empresa, periodo);

    const calcularRTB12Real = (empresa, periodoFinal) => {
        let rtb12 = 0;
        for (let i = 0; i < 12; i++) {
            const p = subtrairMeses(periodoFinal, i);
            rtb12 += obterReceitaBrutaReal(empresa, p);
        }
        return rtb12;
    };

    const getRateioRealizado = (historico, empresa, periodoAnterior) => {
        const recData = historico.find(d => d.Empresa === empresa && d.Periodo === periodoAnterior && d.CC === 'RECEITA') || {};
        const despData = historico.find(d => d.Empresa === empresa && d.Periodo === periodoAnterior && d.CC === 'DESPESA') || {};
        const receitaBruta = parseNum(recData.Credito);
        const das = parseNum(recData.Debito);
        const despesa = Math.abs(parseNum(despData.Saldo_Final));
        const aliquotaEfetiva = receitaBruta > 0 ? (das / receitaBruta) * 100 : 0;
        const recLiquida = receitaBruta - das;
        const resContabil = recLiquida - despesa;
        return { receitaBruta, das, aliquotaEfetiva, despesa, recLiquida, resContabil };
    };

    // --- MOTOR DO CONTROLE DE LIMITE (acúmulo por ano-calendário, 2 linhas) ---
    // Varre janeiro→dezembro do ANO selecionado. Cada ponto tem 2 séries:
    //   • real        → acúmulo REALIZADO (só até o último mês com dado; depois null)
    //   • ritmoAtual  → "se continuar como está": ramifica do último ponto real e soma
    //                    a média mensal realizada até dezembro (PODE passar do limite — é o alerta)
    // Os limites são POR EMPRESA — no consolidado ("Todas") tudo é escalado por nº de empresas.

    const dadosRolling = useMemo(() => {
        if (!dadosHistoricos || dadosHistoricos.length === 0) return [];

        const empresas = empresaRolling === 'Todas' ? EMPRESAS : [empresaRolling];
        const nEmp = empresas.length;
        const ultimoMes = ultimoMesComReceita(dadosHistoricos, empresas, anoRolling);
        const rbaUltimo = acumuladoAnoRealizado(dadosHistoricos, empresas, anoRolling, ultimoMes);
        const mediaMensal = ultimoMes > 0 ? rbaUltimo / ultimoMes : 0;

        const data = [];
        for (let mes = 1; mes <= 12; mes++) {
            const periodoAlvo = `${anoRolling}-${String(mes).padStart(2, '0')}`;
            const realAcum = acumuladoAnoRealizado(dadosHistoricos, empresas, anoRolling, mes);

            let real = null, ritmoAtual = null;
            if (ultimoMes === 0) {
                // ano sem dado nenhum
            } else if (mes < ultimoMes) {
                real = realAcum;
            } else if (mes === ultimoMes) {
                // ponto de ramificação: as 2 linhas se encontram no último mês real
                real = realAcum;
                ritmoAtual = realAcum;
            } else {
                // meses futuros: só a projeção no ritmo atual
                ritmoAtual = rbaUltimo + mediaMensal * (mes - ultimoMes);
            }

            data.push({
                periodoStr: periodoAlvo,
                label: formatarMesAnoCurto(periodoAlvo),
                mes,
                real,
                ritmoAtual,
                pctLimite: real !== null && LIMITE > 0 ? (real / (LIMITE * nEmp)) * 100 : null
            });
        }

        return data;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anoRolling, dadosHistoricos, empresaRolling]);

    // Monta as 7 notas (título + colunas com totalValue) de uma empresa.
    // Fonte única usada tanto pela renderização das tabelas quanto pela exportação em PDF.
    const construirNotasEmpresa = (aba) => {
        // Alíquota PRÓPRIA do prestador (aba) — cada um rateia pela sua.
        const chaveAliq = aba === 'HUNGER' ? 'hunger' : aba === 'P&G' ? 'pg' : aba === 'GC FOOD' ? 'gc' : 'pm';

        const recAliquota = (empOrigem) => {
            const percentual = parseNum(aliquotas[chaveAliq]) / 100;
            return (projecoes[empOrigem] || 0) * percentual;
        };

        // Nota 2 (Saúde Ocupacional): base fixa de R$ 9.205,58 × alíquota PRÓPRIA do prestador.
        // ⚠️ Até 2026-10-29, GC FOOD e P&M usavam aqui a alíquota de OUTRO prestador
        // (GC FOOD pegava a do P&G; P&M a do HUNGER) — exceção herdada da planilha antiga,
        // sem razão de negócio registrada. Removida para GC FOOD e P&M seguirem o rateio
        // próprio, igual às demais notas. HUNGER e P&G já usavam a própria → inalterados.
        // A diferença NÃO some: a PRT é um bolo fechado (Nota 1 = basePRT − Nota 2, via o
        // `diff10` abaixo), então o que sai da Nota 2 entra na Nota 1 do MESMO prestador e
        // o total da PRT não muda.
        const valorNota2 = 9205.58 * (parseNum(aliquotas[chaveAliq]) / 100);

        const basePRT = recAliquota('PRT');
        const diff10 = (basePRT * 0.10) - valorNota2;
        const metadeDif = diff10 / 2;
        const totalManutPRT1 = (basePRT * 0.6204) + metadeDif;
        const totalConsulPRT1 = (basePRT * 0.2796) + metadeDif;

        const basePartner = recAliquota('PARTNER');
        const pctP1 = (aba === 'HUNGER' || aba === 'P&M') ? 0.2495 : 0.2773;
        const pctP2 = (aba === 'HUNGER' || aba === 'P&M') ? 0.7505 : 0.7227;
        const totalPartner1 = basePartner * pctP1;
        const totalPartner2 = basePartner * pctP2;

        return [
            { titulo: 'PRT - Nota 1', colunas: [
                { nome: 'Serv. Manut.', conta: '41160100000003', totalValue: totalManutPRT1 },
                { nome: 'Serv. Consultoria', conta: '41140400000013', totalValue: totalConsulPRT1 }
            ] },
            { titulo: 'PRT - Nota 2', colunas: [
                { nome: 'Segur.Med. Trabalho', conta: '41110600000008', totalValue: valorNota2 }
            ] },
            { titulo: 'PTN', colunas: [
                { nome: 'Serv. Gestão', conta: '41140400000002', totalValue: recAliquota('PTN') }
            ] },
            { titulo: 'PARTNER 1', colunas: [
                { nome: 'Serv. Contábil', conta: '41410100000018', totalValue: totalPartner1 * 0.73 },
                { nome: 'Serv. Consultoria', conta: '41140400000013', totalValue: totalPartner1 * 0.27 }
            ] },
            { titulo: 'PARTNER 2', colunas: [
                { nome: 'Serv. Gestão', conta: '41140400000002', totalValue: totalPartner2 * 0.70 },
                { nome: 'Serv. Consultoria', conta: '41140400000013', totalValue: totalPartner2 * 0.20 },
                { nome: 'Serv. TI', conta: '41180400000003', totalValue: totalPartner2 * 0.10 }
            ] },
            { titulo: 'PSM - MKT', colunas: [
                { nome: 'Promoção', conta: '41250100000001', totalValue: recAliquota('PSM') * 0.75 },
                { nome: 'Serv. Diversos', conta: '41140400000015', totalValue: recAliquota('PSM') * 0.25 }
            ] },
            { titulo: 'PGO - PARTNER', colunas: [
                { nome: 'Serv. Diversos', conta: '41140400000015', totalValue: recAliquota('PGO') }
            ] }
        ];
    };

    // Gera um relatório PDF (via janela de impressão do navegador) organizado POR EMITENTE:
    // uma seção por emitente (as 7 notas), somando o valor de cada prestador para o período.
    const exportarPdfRateioNotas = () => {
        // Títulos das notas (emitentes) — mesma fonte da tela (estáveis entre prestadores).
        const emitentes = construirNotasEmpresa(PRESTADORES[0]).map(n => n.titulo);
        // Pré-calcula as notas de cada prestador uma vez.
        const notasPorPrestador = {};
        PRESTADORES.forEach(p => { notasPorPrestador[p] = construirNotasEmpresa(p); });
        let totalPortfolio = 0;

        const secoes = emitentes.map(emit => {
            let totalEmit = 0;
            const linhas = PRESTADORES.map(p => {
                const nota = notasPorPrestador[p].find(n => n.titulo === emit);
                const valor = nota ? nota.colunas.reduce((acc, c) => acc + c.totalValue, 0) : 0;
                totalEmit += valor;
                return `<tr><td>${p}</td><td class="val">${formatMoney(valor)}</td></tr>`;
            }).join('');
            totalPortfolio += totalEmit;
            return `
                <h2>${emit}</h2>
                <table>
                    <thead><tr><th>Tomador</th><th class="val">Valor Consolidado (R$)</th></tr></thead>
                    <tbody>${linhas}</tbody>
                    <tfoot><tr><td>TOTAL ${emit}</td><td class="val">${formatMoney(totalEmit)}</td></tr></tfoot>
                </table>`;
        }).join('');

        const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8" />
            <title>Rateio Notas - ${formatarDataBr(periodoAtual)}</title>
            <style>
                * { box-sizing: border-box; }
                body { font-family: Arial, Helvetica, sans-serif; color: #1e293b; margin: 32px; }
                .cabecalho { border-bottom: 3px solid #ea580c; padding-bottom: 12px; margin-bottom: 24px; }
                .cabecalho h1 { margin: 0; font-size: 20px; color: #0f172a; }
                .cabecalho .meta { font-size: 12px; color: #64748b; margin-top: 4px; }
                h2 { font-size: 15px; color: #ea580c; margin: 24px 0 8px; text-transform: uppercase; }
                table { width: 100%; border-collapse: collapse; font-size: 13px; page-break-inside: avoid; }
                th, td { border: 1px solid #cbd5e1; padding: 8px 12px; text-align: left; }
                thead th { background: #f1f5f9; color: #0f172a; }
                .val { text-align: right; font-variant-numeric: tabular-nums; }
                tfoot td { background: #fff7ed; font-weight: 800; color: #0f172a; }
                .totalGeral { margin-top: 28px; padding: 14px 18px; background: #0f172a; color: #fff;
                    border-radius: 8px; display: flex; justify-content: space-between; font-size: 15px; font-weight: 800; }
                @media print { body { margin: 16px; } .totalGeral { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
            </style></head><body>
            <div class="cabecalho">
                <h1>Rateio de Notas — RTB12 Valores Consolidados</h1>
                <div class="meta">Período: ${formatarDataBr(periodoAtual)} &nbsp;|&nbsp; Gerado em: ${new Date().toLocaleString('pt-BR')}</div>
            </div>
            ${secoes}
            <div class="totalGeral"><span>TOTAL GERAL (7 EMITENTES)</span><span>R$ ${formatMoney(totalPortfolio)}</span></div>
            </body></html>`;

        const win = window.open('', '_blank');
        if (!win) {
            setMensagem({ type: 'error', text: 'Não foi possível abrir a janela de impressão. Verifique o bloqueador de pop-ups.' });
            setTimeout(() => setMensagem(null), 5000);
            return;
        }
        win.document.write(html);
        win.document.close();
        win.focus();
        win.onload = () => win.print();
    };

    // --- TEMPLATE SAP ---
    // Monta as linhas do lançamento SAP de um prestador, no layout do RTB12_template.xlsx.
    // A base de cada bloco vem da tela Rateio Notas (construirNotasEmpresa); a quebra em
    // contas do SAP segue o MAPA_SAP: `col` usa o valor da coluna da tela; `frac` rateia o
    // total do emitente pela fração fixa. O Total (MC) final = valor da conta × rateio da loja.
    const construirLinhasSapPrestador = (prestador) => {
        const notas = construirNotasEmpresa(prestador);
        const notaTotal = (titulo) => {
            const n = notas.find(x => x.titulo === titulo);
            return n ? n.colunas.reduce((a, c) => a + c.totalValue, 0) : 0;
        };
        const colVal = (titulo, conta) => {
            const n = notas.find(x => x.titulo === titulo);
            if (!n) return 0;
            return n.colunas.filter(c => c.conta === conta).reduce((a, c) => a + c.totalValue, 0);
        };
        const cfg = MAPA_SAP[prestador];
        const lojas = lojasPorEmpresa[prestador] || [];
        const blocos = cfg.blocos.map(bloco => ({
            emitente: bloco.emitente,
            linhas: bloco.rows.map(row => {
                const valorConta = row.fonte.col
                    ? row.fonte.col.reduce((a, [nt, cc]) => a + colVal(nt, cc), 0)
                    : notaTotal(row.fonte.base) * row.fonte.frac;
                return {
                    item: row.item,
                    conta: row.conta,
                    imp: row.imp,
                    desc: SAP_DESC[row.item],
                    valorConta
                };
            })
        }));
        return { filial: cfg.filial, lojas, blocos };
    };

    // Exporta o lançamento SAP em 1 arquivo .xlsx com 4 abas (uma por prestador),
    // replicando o layout do template: rótulo do prestador, blocos por emitente,
    // cabeçalho das colunas e uma linha por (conta × loja).
    const exportarExcelSap = () => {
        const wb = XLSX.utils.book_new();
        ORDEM_PRESTADORES_SAP.forEach(prestador => {
            const dados = construirLinhasSapPrestador(prestador);
            const aoa = [[prestador]];
            dados.blocos.forEach(bloco => {
                aoa.push([bloco.emitente]);
                aoa.push([...COLUNAS_SAP]);
                bloco.linhas.forEach(linha => {
                    dados.lojas.forEach(loja => {
                        // Uma linha por (conta × loja), na ordem exata de COLUNAS_SAP.
                        // Só as 5 colunas da regra levam dado; o resto vai em branco.
                        aoa.push([
                            linha.item,                                             // Nº do item
                            '',                                                     // Descrição do item
                            Number((linha.valorConta * loja.rateio).toFixed(2)),    // Preço unitário (o valor rateado)
                            '',                                                     // % do desconto
                            SAP_UTILIZACAO,                                         // Utilização
                            '', '', '', '', '', '',                                 // Sujeito a IRF · Cód. imposto · CFOP · CST ICMS · Quantidade · Un. medida
                            '',                                                     // Total (MC)
                            '',                                                     // Qtd.confirmada em Dep.
                            linha.conta,                                            // Conta do Razão
                            '',                                                     // Valor do imposto (MC)
                            loja.loja                                               // Regra de distribuição
                        ]);
                    });
                });
                aoa.push([]); // linha em branco entre blocos, como no template
            });
            const ws = XLSX.utils.aoa_to_sheet(aoa);
            // Nome da aba: máx. 31 chars, sem caracteres proibidos pelo Excel.
            const nomeAba = `SAP_${prestador}`.replace(/[\\/?*[\]:]/g, '-').substring(0, 31);
            XLSX.utils.book_append_sheet(wb, ws, nomeAba);
        });
        XLSX.writeFile(wb, `Template_SAP_RTB12_${periodoAtual}.xlsx`);
    };

    // Variáveis Visuais Base
    const periodoMesAnterior = subtrairMeses(periodoAtual, 1);
    const labelPeriodoAtual = formatarDataBr(periodoAtual);
    const labelMesAnteriorNum = periodoMesAnterior.split('-').reverse().join('/');
    const labelMesAnterior = formatarDataBr(periodoMesAnterior);
    // Ano-calendário do período vigente (base do controle de limite do Simples).
    const [anoVigente, mesVigente] = periodoAtual.split('-').map(Number);
    // Meses que ainda faltam para fechar o ano-calendário DEPOIS do mês filtrado
    // (jun → jul..dez = 6). Divisor da "Margem Restante / Mês": distribui a margem
    // que sobra até o limite pelos meses restantes. 0 em dezembro (nada a distribuir).
    const mesesAteFimAno = 12 - mesVigente;

    // Anos com dados na base (+ o ano selecionado), para o seletor da aba Controle do Limite.
    const anosDisponiveis = Array.from(
        new Set([...dadosHistoricos.map(d => d.Periodo && d.Periodo.split('-')[0]).filter(Boolean), String(anoRolling)])
    ).map(Number).sort((a, b) => b - a);

    // --- Referências da aba Controle do Limite (3 linhas) ---
    const empresasRolling = empresaRolling === 'Todas' ? EMPRESAS : [empresaRolling];
    const nEmpRolling = empresasRolling.length;
    // Limites POR EMPRESA, escalados no consolidado ("Todas").
    const subLimiteRolling = SUB_LIMITE * nEmpRolling;
    const limiteRolling = LIMITE * nEmpRolling;
    const ultimoMesRolling = ultimoMesComReceita(dadosHistoricos, empresasRolling, anoRolling);
    // Há o que projetar quando o ano ainda não fechou (existe mês futuro sem dado).
    const ehProjecaoRolling = ultimoMesRolling > 0 && ultimoMesRolling < 12;
    // Último ponto REAL (realizado até o mês com dado) e o ponto de dezembro (fim do ano).
    const rollingAtual = ultimoMesRolling > 0 ? (dadosRolling[ultimoMesRolling - 1] || null) : null;
    const rollingDez = dadosRolling[11] || null;
    const projRitmoFimAno = rollingDez ? (rollingDez.ritmoAtual !== null ? rollingDez.ritmoAtual : rollingDez.real) : null;

    // Tabela de ajuste (todas as empresas): faturamento/mês no ritmo atual + quanto reduzir
    // por mês para fechar o ano no teto efetivo (LIMITE − sobra de 700k = R$ 4,1M).
    const linhasAjuste = EMPRESAS.map(emp => {
        const ultimoMesEmp = ultimoMesComReceita(dadosHistoricos, [emp], anoRolling);
        const r = ritmoEAjuste(dadosHistoricos, emp, anoRolling, ultimoMesEmp, TETO_EFETIVO);
        return { empresa: emp, ...r };
    });

    const tdStyle = { padding: '10px 12px', border: '1px solid #e2e8f0', textAlign: 'right', fontSize: '13px' };
    const thStyle = { padding: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', color: '#1e293b', fontWeight: '700', fontSize: '12px', textAlign: 'center' };

    return (
        <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
            {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}
            <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease', width: '100%' }}>

                {/* CABEÇALHO */}
                {!embedded && (
                <div style={{ height: '85px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '0 20px 0 60px' : '0 40px', position: 'sticky', top: 0, zIndex: 20 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button onClick={() => navigate('/automacoes')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                                <ArrowLeft size={14} /> Voltar
                            </button>
                            <span style={{ margin: '0 4px' }}>/</span> Automação & Processos <span style={{ margin: '0 4px' }}>/</span> Contábil
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <DollarSign size={24} color={corModulo} /> Análise do RTB12
                            </h2>
                            <button
                                type="button"
                                onClick={() => setIsHelpOpen(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: '#f0fdf4', color: corModulo, border: '1px solid #dcfce7', borderRadius: '99px', fontWeight: '600', cursor: 'pointer', fontSize: '12px', transition: 'all 0.2s' }}
                            >
                                <HelpCircle size={14} /> Como Utilizar
                            </button>
                        </div>
                    </div>
                    {!isMobile && (
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#475569' }}>
                            Olá, {nomeUsuario} 👋
                        </span>
                    )}
                </div>
                )}

                <div style={{ padding: isMobile ? '20px' : '40px' }} className="max-w-full mx-auto">

                    {mensagem && (
                        <div style={{ backgroundColor: mensagem.type === 'success' ? '#ecfdf5' : '#fef2f2', border: `1px solid ${mensagem.type === 'success' ? '#a7f3d0' : '#fecaca'}`, color: mensagem.type === 'success' ? '#059669' : '#ef4444', padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', fontSize: '14px' }}>
                            {mensagem.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />} {mensagem.text}
                        </div>
                    )}

                    {!temDadosReais && dadosHistoricos.length > 0 && (
                        <div style={{ padding: '16px 20px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: '#fef2f2', color: '#b91c1c', border: `1px solid #fecaca` }}>
                            <AlertCircle size={24} />
                            <span style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                <strong>Aviso Importante:</strong> Ainda não constam na base os dados de Receita e Despesa para o período vigente ({formatarDataBr(periodoAtual)}). Por favor, alimente a base de dados (via Excel) para garantir a precisão da análise.
                            </span>
                        </div>
                    )}

                    {temProjecao && dadosHistoricos.length > 0 && activeTab !== 'rateio-notas' && activeTab !== 'template-sap' && (
                        <div style={{ padding: '16px 20px', borderRadius: '8px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: '#eff6ff', color: '#1d4ed8', border: `1px solid #bfdbfe` }}>
                            <Info size={24} />
                            <span style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                <strong>Aviso:</strong> Os ajustes de projeção já foram lançados e constam na base para o período de {formatarDataBr(periodoAtual)}.
                            </span>
                        </div>
                    )}

                    {/* ABAS */}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', borderBottom: '2px solid #e2e8f0', paddingBottom: '16px', overflowX: 'auto' }}>
                        <button onClick={() => setActiveTab('resumo')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', backgroundColor: activeTab === 'resumo' ? '#f0fdf4' : 'transparent', color: activeTab === 'resumo' ? corModulo : '#64748b', border: activeTab === 'resumo' ? `1px solid ${corModulo}` : '1px solid transparent', whiteSpace: 'nowrap' }}>
                            <LayoutDashboard size={18} /> Resumo RTB12
                        </button>
                        <button onClick={() => setActiveTab('rateio')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', backgroundColor: activeTab === 'rateio' ? '#f0fdf4' : 'transparent', color: activeTab === 'rateio' ? corModulo : '#64748b', border: activeTab === 'rateio' ? `1px solid ${corModulo}` : '1px solid transparent', whiteSpace: 'nowrap' }}>
                            <PieChart size={18} /> Rateio Orçado
                        </button>
                        <button onClick={() => setActiveTab('rateio-notas')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', backgroundColor: activeTab === 'rateio-notas' ? '#fff7ed' : 'transparent', color: activeTab === 'rateio-notas' ? '#ea580c' : '#64748b', border: activeTab === 'rateio-notas' ? `1px solid #ea580c` : '1px solid transparent', whiteSpace: 'nowrap' }}>
                            <Receipt size={18} /> Rateio Notas
                        </button>
                        <button onClick={() => setActiveTab('template-sap')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', backgroundColor: activeTab === 'template-sap' ? '#f0fdfa' : 'transparent', color: activeTab === 'template-sap' ? corSap : '#64748b', border: activeTab === 'template-sap' ? `1px solid ${corSap}` : '1px solid transparent', whiteSpace: 'nowrap' }}>
                            <FileSpreadsheet size={18} /> Template SAP
                        </button>
                        <button onClick={() => setActiveTab('rolling')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', backgroundColor: activeTab === 'rolling' ? '#f0f9ff' : 'transparent', color: activeTab === 'rolling' ? corRolling : '#64748b', border: activeTab === 'rolling' ? `1px solid ${corRolling}` : '1px solid transparent', whiteSpace: 'nowrap' }}>
                            <TrendingUp size={18} /> Controle do Limite
                        </button>
                        {podeEditarRateio && (
                            <button onClick={() => setActiveTab('controle-rateio')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', backgroundColor: activeTab === 'controle-rateio' ? '#faf5ff' : 'transparent', color: activeTab === 'controle-rateio' ? '#7c3aed' : '#64748b', border: activeTab === 'controle-rateio' ? '1px solid #7c3aed' : '1px solid transparent', whiteSpace: 'nowrap' }}>
                                <SlidersHorizontal size={18} /> Controle de Rateio
                            </button>
                        )}
                    </div>

                    {/* FILTRO E BOTÕES */}
                    <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px', marginBottom: '24px' }}>

                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
                            {/* Aba Controle do Limite filtra por ANO (limite do Simples é anual);
                                as demais abas filtram por mês (Período de Análise). */}
                            {activeTab === 'rolling' ? (
                                <div style={{ flex: 1, minWidth: '150px', maxWidth: '200px' }}>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Ano</label>
                                    <select value={anoRolling} onChange={(e) => setAnoRolling(Number(e.target.value))} style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: '600', color: '#1e293b', outline: 'none' }}>
                                        {anosDisponiveis.map(ano => <option key={ano} value={ano}>{ano}</option>)}
                                    </select>
                                </div>
                            ) : (
                                <div style={{ flex: 1, minWidth: '150px', maxWidth: '200px' }}>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Período de Análise</label>
                                    <input type="month" value={periodoAtual} onChange={handlePeriodoChange} style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: '600', color: '#1e293b', outline: 'none' }} />
                                </div>
                            )}

                            {activeTab === 'rolling' && (
                                <div style={{ flex: 1, minWidth: '150px', maxWidth: '200px' }}>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Empresa</label>
                                    <select value={empresaRolling} onChange={(e) => setEmpresaRolling(e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: '600', color: '#1e293b', outline: 'none' }}>
                                        <option value="Todas">Todas (Consolidado)</option>
                                        {EMPRESAS.map(emp => <option key={emp} value={emp}>{emp}</option>)}
                                    </select>
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <input
                                type="file"
                                id="upload-planilha-rtb12"
                                accept=".xlsx, .xls, .csv"
                                style={{ display: 'none' }}
                                onChange={handleFileUpload}
                            />

                            <button onClick={baixarPlanilhaBase} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '8px', backgroundColor: '#fff', color: '#334155', fontWeight: '600', border: '1px solid #cbd5e1', cursor: 'pointer' }}>
                                <Download size={18} /> Baixar Base
                            </button>

                            <button
                                onClick={() => {
                                    const inputUpload = document.getElementById('upload-planilha-rtb12');
                                    if (inputUpload) {
                                        inputUpload.click();
                                    } else {
                                        alert("Erro: O campo de upload não foi encontrado na tela.");
                                    }
                                }}
                                disabled={carregando}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '8px', backgroundColor: '#f1f5f9', color: '#334155', fontWeight: '600', border: '1px solid #cbd5e1', cursor: 'pointer' }}
                            >
                                <UploadCloud size={18} /> Importar Planilha
                            </button>

                            {temProjecao && activeTab !== 'rolling' && activeTab !== 'rateio-notas' && activeTab !== 'template-sap' && (
                                <button onClick={removerProjecoes} disabled={salvando || carregando} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '8px', backgroundColor: '#fef2f2', color: '#ef4444', fontWeight: '600', border: '1px solid #fecaca', cursor: 'pointer' }}>
                                    <Trash2 size={18} /> Limpar Projeção
                                </button>
                            )}

                            {activeTab !== 'rolling' && activeTab !== 'rateio-notas' && activeTab !== 'template-sap' && (
                                <button onClick={salvarProjecoes} disabled={salvando || carregando} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '8px', backgroundColor: corModulo, color: '#fff', fontWeight: '600', border: 'none', cursor: 'pointer' }}>
                                    {salvando ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Salvar Projeção
                                </button>
                            )}
                        </div>
                    </div>

                    {carregando ? <div style={{ padding: '60px', textAlign: 'center' }}> <Loader2 size={32} className="animate-spin" color={corModulo} /> </div> : (
                        <>
                            {/* ================= ABA 1: RESUMO RTB12 ================= */}
                            {activeTab === 'resumo' && (
                                <div style={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ ...thStyle, textAlign: 'right' }}>Período: {labelPeriodoAtual}</th>
                                                    {EMPRESAS.map(emp => <th key={emp} style={thStyle}>{emp}</th>)}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {/* Base da ALÍQUOTA — RBT12: 12 meses anteriores ao mês de apuração,
                                                    sem o mês vigente. NÃO se mistura com o controle de limite abaixo. */}
                                                <tr style={{ backgroundColor: '#f8fafc' }}>
                                                    <td style={tdStyle}>
                                                        RTB12 {labelMesAnterior}
                                                        <span style={{ display: 'block', fontSize: '10px', fontWeight: '400', color: '#94a3b8' }}>base da alíquota (12 meses anteriores)</span>
                                                    </td>
                                                    {EMPRESAS.map(emp => { const v = calcularRTB12Real(emp, periodoMesAnterior); return <td key={emp} style={{ ...tdStyle, color: v < 0 ? '#ef4444' : '#475569' }}>{formatMoney(v)}</td> })}
                                                </tr>
                                                {/* Controle do LIMITE — por ano-calendário (zera em 31/dez). */}
                                                <tr>
                                                    <td style={tdStyle}>
                                                        Acumulado {anoVigente} até {labelMesAnterior}
                                                        <span style={{ display: 'block', fontSize: '10px', fontWeight: '400', color: '#94a3b8' }}>realizado de jan/{String(anoVigente).slice(2)} ao mês anterior</span>
                                                    </td>
                                                    {EMPRESAS.map(emp => { const v = acumuladoAnoRealizado(dadosHistoricos, [emp], anoVigente, mesVigente - 1); return <td key={emp} style={{ ...tdStyle, color: v < 0 ? '#ef4444' : '#475569' }}>{formatMoney(v)}</td> })}
                                                </tr>
                                                <tr style={{ backgroundColor: '#f0fdfa' }}>
                                                    <td style={{ ...tdStyle, color: '#0f766e', fontWeight: '700' }}>Projeção {labelPeriodoAtual}</td>
                                                    {EMPRESAS.map(emp => <td key={emp} style={{ ...tdStyle, fontWeight: '700', color: '#0f766e' }}>{formatMoney(projecoes[emp])}</td>)}
                                                </tr>
                                                <tr style={{ backgroundColor: '#f8fafc' }}>
                                                    <td style={{ ...tdStyle, fontWeight: '700' }}>
                                                        Acumulado {anoVigente} Projetado
                                                        <span style={{ display: 'block', fontSize: '10px', fontWeight: '400', color: '#94a3b8' }}>realizado do ano + projeção de {labelPeriodoAtual}</span>
                                                    </td>
                                                    {EMPRESAS.map(emp => { const v = acumuladoAnoAteVigente(dadosHistoricos, [emp], periodoAtual, temDadosReais, projecoes); return <td key={emp} style={{ ...tdStyle, fontWeight: '700', color: v < 0 ? '#ef4444' : '#1e293b' }}>{formatMoney(v)}</td> })}
                                                </tr>
                                                <tr>
                                                    <td style={tdStyle}>Margem Restante Sub Limite</td>
                                                    {EMPRESAS.map(emp => { const acum = acumuladoAnoAteVigente(dadosHistoricos, [emp], periodoAtual, temDadosReais, projecoes); const v = SUB_LIMITE - acum; return <td key={emp} style={{ ...tdStyle, color: v < 0 ? '#ef4444' : '#1e293b' }}>{formatMoney(v)}</td> })}
                                                </tr>
                                                <tr>
                                                    <td style={{ ...tdStyle, fontWeight: '700' }}>Margem Restante Limite</td>
                                                    {EMPRESAS.map(emp => { const acum = acumuladoAnoAteVigente(dadosHistoricos, [emp], periodoAtual, temDadosReais, projecoes); const v = LIMITE - acum; return <td key={emp} style={{ ...tdStyle, fontWeight: '700', color: v < 0 ? '#ef4444' : '#0f172a' }}>{formatMoney(v)}</td> })}
                                                </tr>
                                                {/* Margem que ainda cabe até o limite, distribuída pelos meses restantes
                                                    do ano (jun → ÷6). Quanto cada empresa pode faturar/mês de jul a dez
                                                    sem estourar o limite. Em dezembro não há meses a distribuir (mostra —). */}
                                                <tr style={{ backgroundColor: '#f0fdfa' }}>
                                                    <td style={{ ...tdStyle, color: '#0f766e', fontWeight: '700' }}>
                                                        Margem Restante / Mês
                                                        <span style={{ display: 'block', fontSize: '10px', fontWeight: '400', color: '#94a3b8' }}>
                                                            margem restante limite ÷ {mesesAteFimAno > 0 ? `${mesesAteFimAno} ${mesesAteFimAno === 1 ? 'mês restante' : 'meses restantes'} (até dez)` : 'ano fechado'}
                                                        </span>
                                                    </td>
                                                    {EMPRESAS.map(emp => {
                                                        const acum = acumuladoAnoAteVigente(dadosHistoricos, [emp], periodoAtual, temDadosReais, projecoes);
                                                        const margem = LIMITE - acum;
                                                        const v = mesesAteFimAno > 0 ? margem / mesesAteFimAno : null;
                                                        return <td key={emp} style={{ ...tdStyle, fontWeight: '700', color: v === null ? '#94a3b8' : v < 0 ? '#ef4444' : '#0f766e' }}>{v === null ? '—' : formatMoney(v)}</td>;
                                                    })}
                                                </tr>
                                                {/* Situação de enquadramento (por empresa): alerta do MÊS e do FIM DO ANO */}
                                                <tr>
                                                    <td style={{ ...tdStyle, fontWeight: '700' }}>
                                                        Situação (Simples)
                                                        <span style={{ display: 'block', fontSize: '10px', fontWeight: '400', color: '#94a3b8' }}>mês (RBA atual) · fim do ano (no ritmo)</span>
                                                    </td>
                                                    {EMPRESAS.map(emp => {
                                                        // Alerta do MÊS: RBA acumulado até o mês vigente (com projeção do mês).
                                                        const acumMes = acumuladoAnoAteVigente(dadosHistoricos, [emp], periodoAtual, temDadosReais, projecoes);
                                                        const enqMes = enquadramentoRBA(acumMes);
                                                        // Alerta do FIM DO ANO: RBA projetado no ritmo atual (média mensal × 12).
                                                        const ultimoMesEmp = ultimoMesComReceita(dadosHistoricos, [emp], anoVigente);
                                                        const projFim = projecaoFimAnoRitmo(dadosHistoricos, [emp], anoVigente, ultimoMesEmp);
                                                        const enqFim = enquadramentoRBA(projFim);
                                                        const rotulo = (enq) => enq.nivel === 'ok' ? 'OK' : enq.nivel === 'sublimite' ? 'Sub-limite' : enq.nivel === 'excedido' ? 'ACIMA' : 'ESTOURO';
                                                        const badge = (enq, prefixo, titulo) => (
                                                            <span title={titulo} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: '700', color: '#fff', backgroundColor: enq.cor, whiteSpace: 'nowrap' }}>
                                                                {prefixo}: {rotulo(enq)}
                                                            </span>
                                                        );
                                                        return (
                                                            <td key={emp} style={{ ...tdStyle, textAlign: 'center' }}>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                                                    {badge(enqMes, 'Mês', `Mês: ${enqMes.label}`)}
                                                                    {badge(enqFim, 'Ano', `Fim do ano (ritmo atual): ${enqFim.label}`)}
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* ================= ABA 2: RATEIO ORÇADO ================= */}
                            {activeTab === 'rateio' && (
                                <div>
                                    <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '20px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                                        <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}> <Settings size={18} color={corModulo} /> Alíquotas (%) </h3>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                                            {Object.keys(aliquotas).map(k => (
                                                <div key={k}>
                                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b' }}>{k.toUpperCase()}</label>
                                                    <input type="text" value={aliquotas[k]} onChange={(e) => handleAliquotaChange(k, e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                        <div style={{ backgroundColor: '#a3e635', padding: '12px', textAlign: 'center', fontWeight: '800', color: '#166534' }}> RATEIO ORÇADO PARTNERS </div>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
                                                <thead>
                                                    <tr>
                                                        <th rowSpan={2} style={{ ...thStyle, backgroundColor: '#bef264' }}>EMPRESAS</th>
                                                        <th colSpan={6} style={{ ...thStyle, backgroundColor: '#bef264' }}>RECEITA REALIZADA</th>
                                                        <th rowSpan={2} style={{ ...thStyle, backgroundColor: '#bef264' }}>REC ESTIMADA (5%)</th>
                                                        <th rowSpan={2} style={{ ...thStyle, backgroundColor: '#bef264' }}>DAS estimado</th>
                                                        <th rowSpan={2} style={{ ...thStyle, backgroundColor: '#bef264' }}>RESULTADO LIQUIDO</th>
                                                        <th rowSpan={2} style={{ ...thStyle, backgroundColor: '#bef264' }}>DESP. ESTIMADA (5%)</th>
                                                        <th rowSpan={2} style={{ ...thStyle, backgroundColor: '#bef264' }}>LUCRO ESTIMADO</th>
                                                    </tr>
                                                    <tr>
                                                        <th style={{ ...thStyle, backgroundColor: '#bef264' }}>RECEITA BRUTA <br />{labelMesAnteriorNum}</th>
                                                        <th style={{ ...thStyle, backgroundColor: '#bef264' }}>(%) DAS<br />Alíquota efetiva</th>
                                                        <th style={{ ...thStyle, backgroundColor: '#bef264' }}>DAS <br />{labelMesAnteriorNum}</th>
                                                        <th style={{ ...thStyle, backgroundColor: '#bef264' }}>Despesa <br />{labelMesAnteriorNum}</th>
                                                        <th style={{ ...thStyle, backgroundColor: '#bef264' }}>REC LÍQUIDA</th>
                                                        <th style={{ ...thStyle, backgroundColor: '#bef264' }}>RES. CONTÁBIL</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {EMPRESAS.map(emp => {
                                                        const row = getRateioRealizado(dadosHistoricos, emp, periodoMesAnterior);
                                                        const recEstimada = projecoes[emp] || 0;
                                                        const dasEstimado = recEstimada * (row.aliquotaEfetiva / 100);
                                                        const resLiq = recEstimada - dasEstimado;
                                                        const despEst = row.despesa * 1.05;
                                                        const lucroEst = resLiq - despEst;

                                                        return (
                                                            <tr key={emp}>
                                                                <td style={{ ...tdStyle, fontWeight: '700', textAlign: 'center' }}>{emp}</td>
                                                                <td style={tdStyle}>{formatMoney(row.receitaBruta)}</td>
                                                                <td style={tdStyle}>{formatMoney(row.aliquotaEfetiva)}%</td>
                                                                <td style={tdStyle}>{formatMoney(row.das)}</td>
                                                                <td style={tdStyle}>{formatMoney(row.despesa)}</td>
                                                                <td style={tdStyle}>{formatMoney(row.recLiquida)}</td>
                                                                <td style={{ ...tdStyle, color: row.resContabil < 0 ? '#ef4444' : '#1e293b' }}>{formatMoney(row.resContabil)}</td>

                                                                <td style={{ ...tdStyle, backgroundColor: '#f0fdfa' }}>
                                                                    <div style={{ fontWeight: '700', color: '#0f766e' }}>{formatMoney(recEstimada)}</div>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Novo Ajuste +/-"
                                                                        value={ajustesRaw[emp] || (ajustesRateio[emp] === 0 || ajustesRateio[emp] === undefined ? '' : (ajustesRateio[emp] || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 }))}
                                                                        onChange={(e) => handleAjusteChange(emp, e.target.value)}
                                                                        style={{ width: '100%', textAlign: 'right', fontSize: '11px', border: '1px solid #ccfbf1', borderRadius: '4px', outline: 'none', color: '#0f766e', marginTop: '4px', display: temProjecao ? 'none' : 'block' }}
                                                                    />
                                                                </td>

                                                                <td style={tdStyle}>{formatMoney(dasEstimado)}</td>
                                                                <td style={{ ...tdStyle, color: resLiq < 0 ? '#ef4444' : '#1e293b' }}>{formatMoney(resLiq)}</td>
                                                                <td style={tdStyle}>{formatMoney(despEst)}</td>
                                                                <td style={{ ...tdStyle, fontWeight: '700', color: lucroEst < 0 ? '#ef4444' : '#166534' }}>{formatMoney(lucroEst)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* TABELA INFERIOR: ALÍQUOTAS */}
                                    <div style={{ marginTop: '32px', backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                        <div style={{ backgroundColor: '#35e661', padding: '12px', textAlign: 'center', fontWeight: '800', color: '#166534' }}> REC ESTIMADA POR EMPRESA (ALÍQUOTAS) </div>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ ...thStyle, backgroundColor: '#35e661' }}>EMPRESAS</th><th style={{ ...thStyle, backgroundColor: '#35e661' }}>TOTAL ESTIMADO</th>
                                                        <th style={{ ...thStyle, backgroundColor: '#35e661' }}>HUNGER ({aliquotas.hunger}%)</th><th style={{ ...thStyle, backgroundColor: '#35e661' }}>P&G ({aliquotas.pg}%)</th>
                                                        <th style={{ ...thStyle, backgroundColor: '#35e661' }}>GC FOOD ({aliquotas.gc}%)</th><th style={{ ...thStyle, backgroundColor: '#35e661' }}>P&M ({aliquotas.pm}%)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {EMPRESAS.map(emp => {
                                                        const rec = projecoes[emp] || 0;
                                                        return (
                                                            <tr key={`sub-${emp}`}>
                                                                <td style={{ ...tdStyle, fontWeight: '700', textAlign: 'center' }}>{emp}</td>
                                                                <td style={{ ...tdStyle, fontWeight: '700', color: '#0f766e' }}>{formatMoney(rec)}</td>
                                                                <td style={tdStyle}>{formatMoney(rec * (parseNum(aliquotas.hunger) / 100))}</td>
                                                                <td style={tdStyle}>{formatMoney(rec * (parseNum(aliquotas.pg) / 100))}</td>
                                                                <td style={tdStyle}>{formatMoney(rec * (parseNum(aliquotas.gc) / 100))}</td>
                                                                <td style={tdStyle}>{formatMoney(rec * (parseNum(aliquotas.pm) / 100))}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* TABELA FIXA: NFS PRT */}
                                    <div style={{ marginTop: '32px', backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                        <div style={{ backgroundColor: '#f8fafc', padding: '12px 20px', borderBottom: '1px solid #e2e8f0' }}>
                                            <span style={{ fontWeight: '800', fontStyle: 'italic', color: '#475569', fontSize: '14px' }}>Obs: NFS PRT</span>
                                        </div>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ ...thStyle, backgroundColor: '#f1f5f9' }}>EMPRESA</th>
                                                        <th style={{ ...thStyle, backgroundColor: '#f1f5f9' }}>SERVIÇO</th>
                                                        <th style={{ ...thStyle, backgroundColor: '#f1f5f9' }}>HUNGER</th>
                                                        <th style={{ ...thStyle, backgroundColor: '#f1f5f9' }}>P&G</th>
                                                        <th style={{ ...thStyle, backgroundColor: '#f1f5f9' }}>GC</th>
                                                        <th style={{ ...thStyle, backgroundColor: '#f1f5f9' }}>P&M</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600' }}>NOTA 2</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', color: '#475569' }}>SAÚDE OCUPACIONAL</td>
                                                        <td style={{ ...tdStyle, backgroundColor: '#e2e8f0', textAlign: 'center', fontWeight: '700', color: '#334155' }}>48,65%</td>
                                                        <td style={{ ...tdStyle, backgroundColor: '#e2e8f0', textAlign: 'center', fontWeight: '700', color: '#334155' }}>40,36%</td>
                                                        <td style={{ ...tdStyle, backgroundColor: '#e2e8f0', textAlign: 'center', fontWeight: '700', color: '#334155' }}>6,24%</td>
                                                        <td style={{ ...tdStyle, backgroundColor: '#e2e8f0', textAlign: 'center', fontWeight: '700', color: '#334155' }}>4,75%</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '700' }}>PRT</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '700', color: '#0f172a' }}>R$ 9.205,58</td>
                                                        <td style={tdStyle}>{formatMoney(9205.58 * (parseNum(aliquotas.hunger) / 100))}</td>
                                                        <td style={tdStyle}>{formatMoney(9205.58 * (parseNum(aliquotas.pg) / 100))}</td>
                                                        <td style={tdStyle}>{formatMoney(9205.58 * (parseNum(aliquotas.gc) / 100))}</td>
                                                        <td style={tdStyle}>{formatMoney(9205.58 * (parseNum(aliquotas.pm) / 100))}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ================= ABA 3: CONTROLE DO LIMITE (ACÚMULO ANUAL) ================= */}
                            {activeTab === 'rolling' && (
                                <div className="animate-fade-in">
                                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: '24px' }}>

                                    {/* Lado Esquerdo: KPIs e Tabela */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                                        {/* KPI Realizado — acúmulo do ano até o último mês com dado */}
                                        {rollingAtual && (
                                            <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                                    <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#64748b', margin: 0 }}>
                                                        Realizado {anoRolling} (até {rollingAtual.label}) {empresaRolling !== 'Todas' ? `- ${empresaRolling}` : '(Consolidado)'}
                                                    </h3>
                                                    <Info size={16} color="#94a3b8" />
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
                                                    <span style={{ fontSize: '28px', fontWeight: '800', color: corRolling }}>{formatMoney(rollingAtual.real)}</span>
                                                    <span style={{ fontSize: '14px', fontWeight: '700', color: (rollingAtual.pctLimite || 0) >= 100 ? '#e11d48' : (rollingAtual.pctLimite || 0) >= 90 ? '#d97706' : '#16a34a' }}>
                                                        {(rollingAtual.pctLimite || 0).toFixed(1)}% do limite
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* KPI Projeção fim de ano no ritmo atual (alerta) */}
                                        {ehProjecaoRolling && projRitmoFimAno !== null && (
                                            <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                                                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#64748b', margin: '0 0 12px 0' }}>Projeção fim de {anoRolling} — no ritmo atual</h3>
                                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                                                    <span style={{ fontSize: '28px', fontWeight: '800', color: projRitmoFimAno > limiteRolling ? '#e11d48' : projRitmoFimAno > subLimiteRolling ? '#d97706' : '#16a34a' }}>
                                                        {formatMoney(projRitmoFimAno)}
                                                    </span>
                                                    <span style={{ fontSize: '13px', fontWeight: '700', color: projRitmoFimAno > limiteRolling ? '#e11d48' : '#64748b' }}>
                                                        {((projRitmoFimAno / limiteRolling) * 100).toFixed(1)}% {projRitmoFimAno > limiteRolling ? '🔴 estoura' : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Tabela por mês: Real · Ritmo atual */}
                                        <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                                <thead>
                                                    <tr style={{ backgroundColor: corRolling, color: '#fff' }}>
                                                        <th style={{ padding: '10px', textAlign: 'left', fontWeight: '700' }}>Mês</th>
                                                        <th style={{ padding: '10px', textAlign: 'right', fontWeight: '700' }}>Real (acum.)</th>
                                                        <th style={{ padding: '10px', textAlign: 'right', fontWeight: '700', backgroundColor: '#be123c' }}>Ritmo atual (acum.)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {dadosRolling.map((row, i) => (
                                                        <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                            <td style={{ padding: '9px 10px', fontWeight: '600', color: '#334155', whiteSpace: 'nowrap' }}>{row.label}</td>
                                                            <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: '600', color: '#0f172a' }}>{row.real !== null ? formatMoney(row.real) : '-'}</td>
                                                            <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: '700', color: row.ritmoAtual !== null && row.ritmoAtual > limiteRolling ? '#e11d48' : '#be123c' }}>
                                                                {row.ritmoAtual !== null ? formatMoney(row.ritmoAtual) : '-'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Lado Direito: Gráfico — Real · Ritmo atual vs. patamares */}
                                    <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '30px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                                        <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: '#1e293b' }}>Acúmulo de {anoRolling} vs. Limite do Simples {empresaRolling === 'Todas' ? '(consolidado — limites × nº empresas)' : `- ${empresaRolling}`}</h3>
                                        <div style={{ width: '100%', height: '500px' }}>
                                            <ResponsiveContainer>
                                                <LineChart data={dadosRolling} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                                    <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 11 }} />
                                                    <YAxis
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tick={{ fill: '#64748b', fontSize: 11 }}
                                                        tickFormatter={(v) => `R$ ${(v / 1e6).toFixed(1)} Mi`}
                                                        // O topo do eixo garante que a linha do limite sempre apareça.
                                                        domain={[0, (dataMax) => Math.max(dataMax, limiteRolling) * 1.08]}
                                                    />
                                                    <RechartsTooltip
                                                        cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '3 3' }}
                                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontWeight: '600' }}
                                                        formatter={(value, name) => {
                                                            if (name === 'real') return [formatMoney(value), 'Real'];
                                                            if (name === 'ritmoAtual') return [formatMoney(value), 'Ritmo atual'];
                                                            return [formatMoney(value), name];
                                                        }}
                                                    />
                                                    {/* Patamares (por empresa; escalados no consolidado) */}
                                                    <ReferenceLine y={subLimiteRolling} stroke="#d97706" strokeDasharray="4 4" label={{ value: `Sub-limite R$ ${(subLimiteRolling / 1e6).toFixed(1)} Mi`, position: 'insideTopRight', fill: '#d97706', fontSize: 11, fontWeight: 700 }} />
                                                    <ReferenceLine y={limiteRolling} stroke="#e11d48" strokeDasharray="4 4" label={{ value: `Limite R$ ${(limiteRolling / 1e6).toFixed(1)} Mi`, position: 'insideTopRight', fill: '#e11d48', fontSize: 11, fontWeight: 700 }} />
                                                    {/* Linha REAL (sólida) */}
                                                    <Line type="monotone" dataKey="real" stroke={corRolling} strokeWidth={4} dot={{ r: 4, fill: corRolling, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} name="real" connectNulls={false} />
                                                    {/* Linha RITMO ATUAL (tracejada vermelha) — pode passar do limite */}
                                                    {ehProjecaoRolling && (
                                                        <Line type="monotone" dataKey="ritmoAtual" stroke="#e11d48" strokeWidth={3} strokeDasharray="7 7" dot={{ r: 3, fill: '#e11d48', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} name="ritmoAtual" connectNulls={false} />
                                                    )}
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>

                                    </div>

                                    {/* Tabela EMBAIXO: por empresa, faturamento/mês no ritmo atual e ajuste p/ não estourar */}
                                    <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', marginTop: '24px' }}>
                                        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: 0 }}>Faturamento mensal no ritmo atual e ajuste por empresa — {anoRolling}</h3>
                                            <span style={{ fontSize: '12px', color: '#64748b' }}>Ajuste = quanto reduzir por mês para fechar o ano em R$ 4,1 Mi (limite de R$ 4,8 Mi com sobra de R$ 700 mil).</span>
                                        </div>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '640px' }}>
                                                <thead>
                                                    <tr style={{ backgroundColor: '#f8fafc' }}>
                                                        <th style={{ ...thStyle, textAlign: 'left' }}>Empresa</th>
                                                        <th style={thStyle}>Faturamento/mês (ritmo atual)</th>
                                                        <th style={thStyle}>Projeção fim de ano (ritmo)</th>
                                                        <th style={{ ...thStyle, backgroundColor: '#fef2f2' }}>Ajuste/mês p/ não estourar</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {linhasAjuste.map(l => {
                                                        const enqFim = enquadramentoRBA(l.projFimAnoRitmo);
                                                        const precisaAjuste = l.ajusteMensal > 0;
                                                        return (
                                                            <tr key={l.empresa} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '700' }}>{l.empresa}</td>
                                                                <td style={tdStyle}>{formatMoney(l.mediaMensal)}</td>
                                                                <td style={{ ...tdStyle, fontWeight: '700', color: enqFim.cor }} title={enqFim.label}>{formatMoney(l.projFimAnoRitmo)}</td>
                                                                <td style={{ ...tdStyle, backgroundColor: precisaAjuste ? '#fef2f2' : '#f0fdf4', fontWeight: '700', color: precisaAjuste ? '#e11d48' : '#16a34a' }}>
                                                                    {l.mesesRestantes === 0 ? '— (ano fechado)' : precisaAjuste ? `↓ ${formatMoney(l.ajusteMensal)}` : '— (sem ajuste)'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                </div>
                            )}

                            {/* ================= ABA 4: RATEIO NOTAS (NOVA ABA) ================= */}
                            {activeTab === 'rateio-notas' && (
                                <div className="animate-fade-in">
                                    {(() => {
                                        // Emitentes (as 7 notas) — mesma fonte da tela e do PDF.
                                        const emitentes = construirNotasEmpresa(PRESTADORES[0]).map(n => n.titulo);
                                        const emit = empresaRateioNotas;
                                        // Para o emitente selecionado, as colunas (contas) de cada prestador.
                                        const porPrestador = PRESTADORES.map(p => {
                                            const nota = construirNotasEmpresa(p).find(n => n.titulo === emit);
                                            return { prestador: p, colunas: nota ? nota.colunas : [] };
                                        });
                                        // Estrutura das colunas (nome/conta) — estável entre prestadores.
                                        const colDefs = (porPrestador[0].colunas || []).map(c => ({ nome: c.nome, conta: c.conta }));
                                        const totaisColuna = colDefs.map((_, idx) => porPrestador.reduce((acc, pp) => acc + (pp.colunas[idx]?.totalValue || 0), 0));
                                        const totalGeral = totaisColuna.reduce((a, b) => a + b, 0);

                                        return (
                                            <>
                                                {/* Filtro por EMITENTE (as 7 notas) + exportar PDF */}
                                                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                    {emitentes.map(nome => (
                                                        <button
                                                            key={nome}
                                                            onClick={() => setEmpresaRateioNotas(nome)}
                                                            style={{
                                                                padding: '8px 16px', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s',
                                                                backgroundColor: empresaRateioNotas === nome ? '#ea580c' : '#fff',
                                                                color: empresaRateioNotas === nome ? '#fff' : '#64748b',
                                                                border: empresaRateioNotas === nome ? '1px solid #ea580c' : '1px solid #cbd5e1'
                                                            }}
                                                        >
                                                            {nome}
                                                        </button>
                                                    ))}
                                                    <button
                                                        onClick={exportarPdfRateioNotas}
                                                        title="Gera um PDF com o valor consolidado de cada emitente (somando os 4 prestadores)"
                                                        style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', backgroundColor: '#fff', color: '#ea580c', border: '1px solid #ea580c' }}
                                                    >
                                                        <Download size={16} /> Exportar PDF
                                                    </button>
                                                </div>

                                                {/* Tabela por emitente: agrupada por prestador, com as lojas detalhadas */}
                                                {(() => {
                                                    const totalCols = 3 + colDefs.length + 1; // LOJA + C/C + RATEIO + contas + TOTAL
                                                    return (
                                                        <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                                            <div style={{ backgroundColor: '#f8fafc', padding: '12px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#334155' }}>Emitente: {emit}</h3>
                                                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f766e' }}>TOTAL: {formatMoney(totalGeral)}</span>
                                                            </div>
                                                            <div style={{ overflowX: 'auto' }}>
                                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '760px' }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={thStyle}>LOJA</th>
                                                                            <th style={thStyle}>C/C</th>
                                                                            <th style={thStyle}>RATEIO</th>
                                                                            {colDefs.map((col, idx) => (
                                                                                <th key={idx} style={{ ...thStyle, backgroundColor: '#f1f5f9', color: '#0f172a' }}>
                                                                                    {col.nome} <br /><span style={{ fontSize: '10px', fontWeight: 'normal', color: '#64748b' }}>{col.conta}</span>
                                                                                </th>
                                                                            ))}
                                                                            <th style={{ ...thStyle, backgroundColor: '#f1f5f9', color: '#0f172a' }}>TOTAL</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {porPrestador.map((pp) => {
                                                                            const totalPrest = pp.colunas.reduce((acc, c) => acc + c.totalValue, 0);
                                                                            const lojas = lojasPorEmpresa[pp.prestador] || [];
                                                                            return (
                                                                                <React.Fragment key={pp.prestador}>
                                                                                    {/* Faixa do prestador (subtotal consolidado) */}
                                                                                    <tr style={{ backgroundColor: '#f1f5f9' }}>
                                                                                        <td colSpan={totalCols} style={{ ...tdStyle, borderTop: '2px solid #cbd5e1' }}>
                                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                                <span style={{ fontWeight: '800', color: '#334155', textTransform: 'uppercase' }}>{pp.prestador}</span>
                                                                                                <span style={{ fontWeight: '700', color: '#0f766e' }}>TOTAL: {formatMoney(totalPrest)}</span>
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                    {/* Lojas do prestador (rateio aplicado a cada conta) */}
                                                                                    {lojas.length === 0 ? (
                                                                                        <tr><td colSpan={totalCols} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>Sem lojas cadastradas para {pp.prestador}</td></tr>
                                                                                    ) : lojas.map((loja, i) => (
                                                                                        <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                                                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600' }}>{loja.loja}</td>
                                                                                            <td style={{ ...tdStyle, textAlign: 'center' }}>{loja.cc}</td>
                                                                                            <td style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>{(loja.rateio * 100).toFixed(2).replace('.', ',')}%</td>
                                                                                            {colDefs.map((_, idx) => (
                                                                                                <td key={idx} style={{ ...tdStyle, textAlign: 'center' }}>{formatMoney((pp.colunas[idx]?.totalValue || 0) * loja.rateio)}</td>
                                                                                            ))}
                                                                                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600', color: '#0f172a' }}>{formatMoney(totalPrest * loja.rateio)}</td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </React.Fragment>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                    <tfoot>
                                                                        <tr style={{ backgroundColor: '#fff7ed', borderTop: '2px solid #cbd5e1' }}>
                                                                            <td colSpan={3} style={{ ...tdStyle, textAlign: 'right', fontWeight: '800', color: '#0f172a' }}>TOTAL GERAL:</td>
                                                                            {totaisColuna.map((t, idx) => (
                                                                                <td key={idx} style={{ ...tdStyle, textAlign: 'center', fontWeight: '800', color: '#0f766e' }}>{formatMoney(t)}</td>
                                                                            ))}
                                                                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '800', color: '#0f172a' }}>{formatMoney(totalGeral)}</td>
                                                                        </tr>
                                                                    </tfoot>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* ================= ABA: TEMPLATE SAP ================= */}
                            {activeTab === 'template-sap' && (
                                <div className="animate-fade-in">
                                    {(() => {
                                        const dados = construirLinhasSapPrestador(prestadorSap);
                                        const totalPrestador = dados.blocos.reduce((acc, b) =>
                                            acc + b.linhas.reduce((a2, l) =>
                                                a2 + dados.lojas.reduce((a3, loja) => a3 + l.valorConta * loja.rateio, 0), 0), 0);

                                        return (
                                            <>
                                                {/* Seletor de prestador (as 4 abas do template) + export */}
                                                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                    {ORDEM_PRESTADORES_SAP.map(p => (
                                                        <button
                                                            key={p}
                                                            onClick={() => setPrestadorSap(p)}
                                                            style={{
                                                                padding: '8px 16px', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s',
                                                                backgroundColor: prestadorSap === p ? corSap : '#fff',
                                                                color: prestadorSap === p ? '#fff' : '#64748b',
                                                                border: prestadorSap === p ? `1px solid ${corSap}` : '1px solid #cbd5e1'
                                                            }}
                                                        >
                                                            {p}
                                                        </button>
                                                    ))}
                                                    <button
                                                        onClick={exportarExcelSap}
                                                        title="Gera 1 arquivo Excel com 4 abas (uma por prestador), no layout de lançamento SAP"
                                                        style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', backgroundColor: corSap, color: '#fff', border: `1px solid ${corSap}` }}
                                                    >
                                                        <Download size={16} /> Exportar Excel (SAP)
                                                    </button>
                                                </div>

                                                {/* Nota explicativa do modelo */}
                                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px', fontSize: '13px', color: '#0f766e' }}>
                                                    <Info size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
                                                    <span>
                                                        Lançamento SAP do período <strong>{labelPeriodoAtual}</strong>. Os valores vêm da aba <strong>Rateio Notas</strong> (base por emitente), re-quebrados nas contas do SAP e distribuídos por loja pelo rateio. Filial: <strong>{dados.filial}</strong>. O arquivo sai com o <strong>valor rateado no “Preço unitário”</strong> e só estas colunas preenchidas — as demais do layout vão <strong>em branco</strong> para o SAP completar.
                                                    </span>
                                                </div>

                                                {/* Um cartão por bloco de emitente */}
                                                {dados.blocos.map((bloco, bi) => {
                                                    const totalBloco = bloco.linhas.reduce((a2, l) =>
                                                        a2 + dados.lojas.reduce((a3, loja) => a3 + l.valorConta * loja.rateio, 0), 0);
                                                    return (
                                                        <div key={bi} style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '16px' }}>
                                                            <div style={{ backgroundColor: '#f8fafc', padding: '12px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#334155' }}>Emitente: {bloco.emitente}</h3>
                                                                <span style={{ fontSize: '13px', fontWeight: '700', color: corSap }}>TOTAL: {formatMoney(totalBloco)}</span>
                                                            </div>
                                                            <div style={{ overflowX: 'auto' }}>
                                                                {/* Colunas = as 5 que o arquivo SAP realmente preenche (ver COLUNAS_SAP).
                                                                    As demais saem em branco no Excel e por isso não aparecem aqui. */}
                                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '720px' }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={thStyle}>Nº do item</th>
                                                                            <th style={thStyle}>Preço unitário</th>
                                                                            <th style={thStyle}>Utilização</th>
                                                                            <th style={thStyle}>Conta do Razão</th>
                                                                            <th style={thStyle}>Regra de distribuição</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {bloco.linhas.map((l, li) => (
                                                                            <React.Fragment key={li}>
                                                                                {dados.lojas.map((loja, lj) => (
                                                                                    <tr key={`${li}-${lj}`} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: li % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                                                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600' }}>{l.item}</td>
                                                                                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600', color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(l.valorConta * loja.rateio)}</td>
                                                                                        <td style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>{SAP_UTILIZACAO}</td>
                                                                                        <td style={{ ...tdStyle, textAlign: 'center', fontFamily: 'monospace' }}>{l.conta}</td>
                                                                                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600' }}>{loja.loja}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {/* Total geral do prestador */}
                                                <div style={{ marginTop: '8px', padding: '14px 20px', backgroundColor: '#0f172a', color: '#fff', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px', fontWeight: '800' }}>
                                                    <span>TOTAL {prestadorSap}</span>
                                                    <span>R$ {formatMoney(totalPrestador)}</span>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* ================= ABA 5: CONTROLE DE RATEIO ================= */}
                            {activeTab === 'controle-rateio' && podeEditarRateio && (
                                <div className="animate-fade-in">

                                    {/* ── MODAL DE TRANSFERÊNCIA ── */}
                                    {modalTransf && (
                                        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                                            <div style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '32px', width: '100%', maxWidth: '480px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                                                    <div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                            <ArrowRightLeft size={20} color="#7c3aed" />
                                                            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#1e293b' }}>Transferir Loja</h3>
                                                        </div>
                                                        <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                                                            Mova a loja para outro grupo e defina o novo percentual de rateio.
                                                        </p>
                                                    </div>
                                                    <button onClick={() => setModalTransf(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}>
                                                        <X size={20} />
                                                    </button>
                                                </div>

                                                {/* Info da loja */}
                                                <div style={{ backgroundColor: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                                                        <div>
                                                            <span style={{ color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', fontSize: '11px' }}>Loja</span>
                                                            <p style={{ margin: '2px 0 0', fontWeight: '800', color: '#0f172a', fontSize: '18px' }}>{modalTransf.loja}</p>
                                                        </div>
                                                        <div>
                                                            <span style={{ color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', fontSize: '11px' }}>C/C</span>
                                                            <p style={{ margin: '2px 0 0', fontWeight: '700', color: '#475569', fontSize: '14px' }}>{modalTransf.cc}</p>
                                                        </div>
                                                        <div>
                                                            <span style={{ color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', fontSize: '11px' }}>Grupo Atual</span>
                                                            <p style={{ margin: '2px 0 0', fontWeight: '700', color: '#7c3aed', fontSize: '14px' }}>{modalTransf.empresaOrigem}</p>
                                                        </div>
                                                        <div>
                                                            <span style={{ color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', fontSize: '11px' }}>% Atual neste grupo</span>
                                                            <p style={{ margin: '2px 0 0', fontWeight: '700', color: '#475569', fontSize: '14px' }}>
                                                                {(modalTransf.percentualAtual * 100).toFixed(4).replace('.', ',')}%
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Seleção do destino */}
                                                <div style={{ marginBottom: '16px' }}>
                                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', marginBottom: '6px' }}>
                                                        Grupo Destino
                                                    </label>
                                                    <select
                                                        value={modalTransf.empresaDestino}
                                                        onChange={(e) => setModalTransf(prev => ({ ...prev, empresaDestino: e.target.value }))}
                                                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: '600', color: '#1e293b', outline: 'none' }}
                                                    >
                                                        <option value="">-- Selecione o grupo destino --</option>
                                                        {Object.keys(lojasPorEmpresa)
                                                            .filter(g => g !== modalTransf.empresaOrigem)
                                                            .map(g => <option key={g} value={g}>{g}</option>)}
                                                    </select>
                                                </div>

                                                {/* Novo percentual */}
                                                <div style={{ marginBottom: '20px' }}>
                                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', marginBottom: '6px' }}>
                                                        Novo % de Rateio no Grupo Destino
                                                    </label>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <input
                                                            type="text"
                                                            placeholder="Ex: 25,00"
                                                            value={modalTransf.percentualNovo}
                                                            onChange={(e) => setModalTransf(prev => ({ ...prev, percentualNovo: e.target.value.replace(/[^0-9,]/g, '') }))}
                                                            style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid #a78bfa', fontSize: '14px', fontWeight: '600', color: '#1e293b', outline: 'none' }}
                                                        />
                                                        <span style={{ fontSize: '16px', fontWeight: '700', color: '#64748b' }}>%</span>
                                                    </div>
                                                </div>

                                                {/* Aviso */}
                                                <div style={{ backgroundColor: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px', marginBottom: '20px', fontSize: '12px', color: '#92400e', lineHeight: '1.5' }}>
                                                    <strong>⚠ Atenção:</strong> Após confirmar, rebalanceie manualmente os percentuais do grupo de origem ({modalTransf.empresaOrigem}) para que a soma volte a 100%.
                                                </div>

                                                {/* Botões */}
                                                <div style={{ display: 'flex', gap: '12px' }}>
                                                    <button
                                                        onClick={() => setModalTransf(null)}
                                                        style={{ flex: 1, padding: '12px', borderRadius: '8px', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '700', border: '1px solid #cbd5e1', cursor: 'pointer', fontSize: '14px' }}
                                                    >
                                                        Cancelar
                                                    </button>
                                                    <button
                                                        onClick={confirmarTransferencia}
                                                        disabled={!modalTransf.empresaDestino || !modalTransf.percentualNovo || modalTransf.confirmando}
                                                        style={{ flex: 1, padding: '12px', borderRadius: '8px', backgroundColor: !modalTransf.empresaDestino || !modalTransf.percentualNovo ? '#c4b5fd' : '#7c3aed', color: '#fff', fontWeight: '700', border: 'none', cursor: !modalTransf.empresaDestino || !modalTransf.percentualNovo ? 'not-allowed' : 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                                    >
                                                        {modalTransf.confirmando ? <Loader2 size={16} className="animate-spin" /> : <ArrowRightLeft size={16} />}
                                                        Confirmar Transferência
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* ── CABEÇALHO E BOTÃO SALVAR ── */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <SlidersHorizontal size={20} color="#7c3aed" /> Controle de Rateio por Grupo
                                            </h3>
                                            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                                                Edite os percentuais ou transfira lojas entre grupos. A soma de cada grupo deve fechar em 100%.
                                            </p>
                                        </div>
                                        <button
                                            onClick={salvarRateio}
                                            disabled={salvandoRateio || carregandoRateio}
                                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '8px', backgroundColor: '#7c3aed', color: '#fff', fontWeight: '700', border: 'none', cursor: 'pointer', fontSize: '14px' }}
                                        >
                                            {salvandoRateio ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                                            Salvar Percentuais
                                        </button>
                                    </div>

                                    {carregandoRateio ? (
                                        <div style={{ padding: '60px', textAlign: 'center' }}>
                                            <Loader2 size={32} className="animate-spin" color="#7c3aed" />
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                            {Object.entries(lojasPorEmpresa).map(([grupo, lojas]) => {
                                                // Calcula a soma com as edições em tempo real
                                                const somaAtual = lojas.reduce((acc, l) => {
                                                    const editado = rateioEditando[l.id];
                                                    const valor = editado !== undefined && editado !== ''
                                                        ? parseFloat(editado.replace(',', '.')) / 100
                                                        : l.rateio;
                                                    return acc + (isNaN(valor) ? 0 : valor);
                                                }, 0);
                                                const somaOk = Math.abs(somaAtual - 1) < 0.0001;
                                                const somaExcede = somaAtual > 1.00005;
                                                const faltaPct = (1 - somaAtual) * 100;
                                                const barColor = somaOk ? '#16a34a' : somaExcede ? '#dc2626' : '#d97706';
                                                const barWidth = Math.min(somaAtual * 100, 100);

                                                return (
                                                    <div key={grupo} style={{ backgroundColor: '#fff', borderRadius: '16px', border: `1px solid ${somaOk ? '#bbf7d0' : somaExcede ? '#fecaca' : '#e2e8f0'}`, overflow: 'hidden', transition: 'border-color 0.3s' }}>

                                                        {/* Cabeçalho do grupo */}
                                                        <div style={{ backgroundColor: '#faf5ff', padding: '16px 20px', borderBottom: '1px solid #ede9fe' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#4c1d95' }}>{grupo}</h4>
                                                                <span style={{ fontSize: '13px', fontWeight: '800', color: barColor }}>
                                                                    {somaOk
                                                                        ? '✓ 100,00% — Correto'
                                                                        : somaExcede
                                                                            ? `Excede em ${Math.abs(faltaPct).toFixed(4).replace('.', ',')}%`
                                                                            : `Falta ${faltaPct.toFixed(4).replace('.', ',')}% para 100%`
                                                                    }
                                                                </span>
                                                            </div>

                                                            {/* Barra de progresso */}
                                                            <div style={{ height: '8px', backgroundColor: '#ede9fe', borderRadius: '999px', overflow: 'hidden' }}>
                                                                <div style={{ height: '100%', width: `${barWidth}%`, backgroundColor: barColor, borderRadius: '999px', transition: 'width 0.25s ease, background-color 0.25s ease' }} />
                                                            </div>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '11px', color: '#a78bfa' }}>
                                                                <span>0%</span>
                                                                <span style={{ fontWeight: '700', color: barColor }}>{(somaAtual * 100).toFixed(4).replace('.', ',')}% preenchido</span>
                                                                <span>100%</span>
                                                            </div>
                                                        </div>

                                                        {/* Tabela de lojas */}
                                                        <div style={{ overflowX: 'auto' }}>
                                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                                                <thead>
                                                                    <tr style={{ backgroundColor: '#f8fafc' }}>
                                                                        <th style={{ ...thStyle, textAlign: 'center' }}>LOJA</th>
                                                                        <th style={{ ...thStyle, textAlign: 'center' }}>C/C</th>
                                                                        <th style={{ ...thStyle, textAlign: 'center' }}>% ATUAL</th>
                                                                        <th style={{ ...thStyle, textAlign: 'center', color: '#7c3aed' }}>NOVO % RATEIO</th>
                                                                        <th style={{ ...thStyle, textAlign: 'center', color: '#7c3aed' }}>PARCIAL ACUM.</th>
                                                                        <th style={{ ...thStyle, textAlign: 'center' }}>AÇÕES</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(() => {
                                                                        let acumulado = 0;
                                                                        return lojas.map((loja, i) => {
                                                                            const editado = rateioEditando[loja.id];
                                                                            const temEdicao = editado !== undefined && editado !== '';
                                                                            const valorAtivo = temEdicao
                                                                                ? parseFloat(editado.replace(',', '.')) / 100
                                                                                : loja.rateio;
                                                                            acumulado += isNaN(valorAtivo) ? 0 : valorAtivo;
                                                                            const acumOk = Math.abs(acumulado - 1) < 0.0001 && i === lojas.length - 1;

                                                                            return (
                                                                                <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: temEdicao ? '#faf5ff' : (i % 2 === 0 ? '#fff' : '#f8fafc') }}>
                                                                                    <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '700' }}>{loja.loja}</td>
                                                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>{loja.cc}</td>
                                                                                    <td style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>
                                                                                        {(loja.rateio * 100).toFixed(4).replace('.', ',')}%
                                                                                    </td>
                                                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                                                        {loja.id !== undefined ? (
                                                                                            <input
                                                                                                type="text"
                                                                                                placeholder={(loja.rateio * 100).toFixed(4).replace('.', ',')}
                                                                                                value={editado !== undefined ? editado : ''}
                                                                                                onChange={(e) => handleRateioChange(loja.id, e.target.value)}
                                                                                                style={{ width: '100px', padding: '6px 8px', borderRadius: '6px', border: temEdicao ? '2px solid #7c3aed' : '1px solid #cbd5e1', textAlign: 'center', fontSize: '13px', outline: 'none', color: '#1e293b', transition: 'border-color 0.2s' }}
                                                                                            />
                                                                                        ) : (
                                                                                            <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '700', color: acumOk ? '#16a34a' : '#475569' }}>
                                                                                        {(acumulado * 100).toFixed(4).replace('.', ',')}%
                                                                                    </td>
                                                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                                                        {loja.id !== undefined && (
                                                                                            <button
                                                                                                onClick={() => setModalTransf({
                                                                                                    id: loja.id,
                                                                                                    loja: loja.loja,
                                                                                                    cc: loja.cc,
                                                                                                    empresaOrigem: grupo,
                                                                                                    percentualAtual: loja.rateio,
                                                                                                    empresaDestino: '',
                                                                                                    percentualNovo: '',
                                                                                                    confirmando: false
                                                                                                })}
                                                                                                title="Mover para outro grupo"
                                                                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #ddd6fe', backgroundColor: '#faf5ff', color: '#7c3aed', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                                                                                            >
                                                                                                <ArrowRightLeft size={13} /> Mover
                                                                                            </button>
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        });
                                                                    })()}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* ── LOG DE AUDITORIA ── */}
                                    <div style={{ marginTop: '40px', backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                        <div style={{ backgroundColor: '#f8fafc', padding: '12px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <History size={18} color="#64748b" />
                                            <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#334155' }}>Histórico de Alterações</h4>
                                        </div>
                                        {carregandoLog ? (
                                            <div style={{ padding: '40px', textAlign: 'center' }}>
                                                <Loader2 size={24} className="animate-spin" color="#94a3b8" />
                                            </div>
                                        ) : logRateio.length === 0 ? (
                                            <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
                                                Nenhuma alteração registrada ainda.
                                            </div>
                                        ) : (
                                            <div style={{ overflowX: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                                    <thead>
                                                        <tr style={{ backgroundColor: '#f8fafc' }}>
                                                            <th style={{ ...thStyle }}>DATA / HORA</th>
                                                            <th style={{ ...thStyle }}>USUÁRIO</th>
                                                            <th style={{ ...thStyle }}>TIPO</th>
                                                            <th style={{ ...thStyle }}>LOJA</th>
                                                            <th style={{ ...thStyle }}>GRUPO ORIGEM</th>
                                                            <th style={{ ...thStyle }}>GRUPO DESTINO</th>
                                                            <th style={{ ...thStyle }}>% ANTERIOR</th>
                                                            <th style={{ ...thStyle }}>% NOVO</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {logRateio.map((entry, i) => {
                                                            const dataFormatada = entry.data_alteracao
                                                                ? new Date(entry.data_alteracao).toLocaleString('pt-BR')
                                                                : '-';
                                                            const isTransf = entry.tipo === 'TRANSFERENCIA';
                                                            return (
                                                                <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: isTransf ? '#faf5ff' : (i % 2 === 0 ? '#fff' : '#f8fafc') }}>
                                                                    <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap', fontSize: '12px' }}>{dataFormatada}</td>
                                                                    <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600' }}>{entry.usuario_nome}</td>
                                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                                        <span style={{ padding: '3px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '700', backgroundColor: isTransf ? '#ede9fe' : '#f0fdf4', color: isTransf ? '#7c3aed' : '#16a34a' }}>
                                                                            {isTransf ? '↕ Transferência' : '✎ Rateio'}
                                                                        </span>
                                                                    </td>
                                                                    <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '700' }}>{entry.loja}</td>
                                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>{entry.empresa}</td>
                                                                    <td style={{ ...tdStyle, textAlign: 'center', color: isTransf ? '#7c3aed' : '#94a3b8' }}>
                                                                        {entry.empresa_destino || '—'}
                                                                    </td>
                                                                    <td style={{ ...tdStyle, textAlign: 'center', color: '#ef4444' }}>
                                                                        {(parseFloat(entry.percentual_anterior) * 100).toFixed(4).replace('.', ',')}%
                                                                    </td>
                                                                    <td style={{ ...tdStyle, textAlign: 'center', color: '#16a34a', fontWeight: '700' }}>
                                                                        {(parseFloat(entry.percentual_novo) * 100).toFixed(4).replace('.', ',')}%
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <HelpCenter isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} secaoInicial="rtb12-simplesnacional" showOnlyIds={['rtb12-simplesnacional']} />
        </div>
    );
};

export default RTB12SimplesNacional;